import {
  ARENA_DEFAULT_BUY_IN_TIMING,
  OrderType,
  OrderStatus,
  ExchangeStatus,
  CancelOrderStatus,
  OrderBookMetrics,
  EXCHANGE_ACCOUNT_DISCONNECTION_REASONS,
  TradeToEarnCampaign,
  TradeToEarnCampaignWithStats,
  TradeToEarnCampaignSummary,
  CampaignAnalyticsEpochGroups,
  CampaignVaultDetail,
  CampaignAnalyticsEpochs,
  CampaignAnalyticsListItem,
  CampaignVaultListItem,
  AdminMarkArenaEntryPaidResponse,
  ArenaOpsOverviewKpisResponse,
  ArenaOpsOverviewCoverageRingsResponse,
  ArenaOpsOverviewReconciliationResponse,
  ArenaOpsUsersResponse,
  ArenaOpsUsersKpisResponse,
  ArenaOpsUsersLedgerIdentityResponse,
  ArenaOpsUsersBalanceDistributionResponse,
  ArenaOpsUsersMovementsResponse,
  ArenaOpsMoneyPeriodFlowsResponse,
  ArenaOpsMoneyHouseInjectionResponse,
  ArenaOpsMoneyRakeProvenanceResponse,
  ArenaOpsMoneyRevenuePerDayResponse,
  ArenaOpsMoneyDepositsResponse,
  ArenaOpsMoneyWithdrawalsResponse,
  ArenaOpsMoneyFreeTicketValueResponse,
  ArenaOpsMoneySankeyResponse,
  ArenaOpsMoneyMovementsResponse,
  ArenaOpsMoneyWithdrawalRequestsResponse,
  ArenaOpsTicketsLifecycleResponse,
  ArenaOpsTicketsBySourceResponse,
  ArenaOpsTicketLinksResponse,
  ArenaOpsTicketsOutcomesResponse,
  ArenaOpsMatchmakingUniverseResponse,
  ArenaOpsMatchmakingPairingRateSeriesResponse,
  ArenaOpsMatchmakingWaitSeriesResponse,
  ArenaOpsMatchmakingWaitCdfResponse,
  ArenaOpsMatchmakingEntriesByBuyInResponse,
  ArenaOpsMatchmakingQueueHealthResponse,
  ArenaOpsMatchmakingMatchesByHourResponse,
  ArenaOpsMatchmakingQueueDepthResponse,
  AdminCancelArenaEntryResponse,
  GetCedeArenaCompetitionBlueprintResponse,
  LeaderboardSimulateResponse,
  QUEST_REWARD_TYPE,
  REFERRAL_STATUS,
  TradeToEarnLeaderboardDto,
  SOCIAL_NETWORK,
  SOCIAL_STATUS,
  IssueSeverity,
  IssueType,
  QUEST_TYPE,
  QUEST_FREQUENCY,
  QUEST_APP,
  QUEST_REWARD_DISTRIBUTION,
  QUEST_WINDOW_ANCHOR,
  PublicationStatus,
  REWARD_NETWORK,
  HteCampaignFullDataDto,
  type VolumeTier,
  type PointBoostTier,
  CANONICAL_ARENA_QUALIFICATION_QUESTIONS,
  type ArenaQualificationQuestionDto,
  type CedeFlowTradeAndEarnCompetitionDto,
} from "@cede/types";
import { forgeNetworkURL } from "../cdn-client";
import { CEDE_CEXS } from "../cexs";
import { EXISTING_FIAT_CURRENCIES } from "../fiat";
import { API_ENDPOINT } from "./endpoints";
import type { APIResponseMap } from "./types";
import Decimal from "decimal.js";

/**
 * The real set, not a hand-trimmed copy: the client renders option ids from
 * code, and a shortened mock renders tiles the mocked config does not contain.
 */
const MOCK_ARENA_QUALIFICATION_QUESTIONS: ArenaQualificationQuestionDto[] = [
  ...CANONICAL_ARENA_QUALIFICATION_QUESTIONS,
];

/** A fixed Monday→Monday UTC week so mocked weekly responses stay deterministic. */
const MOCK_ARENA_WEEK = {
  weekStart: "2026-01-05T00:00:00.000Z",
  weekEnd: "2026-01-12T00:00:00.000Z",
};

const MOCK_ARENA_AFFILIATE_APPLICATION = {
  id: 1,
  name: "Mock Creator",
  email: "creator@example.com",
  telegram: null,
  country: "FR",
  regionCommunities: ["EUROPE" as const],
  socials: { x: "https://x.com/mock-creator" },
  heardAboutUs: "X" as const,
  applicationType: "CREATOR" as const,
  dealNotes: null,
  status: "NEW" as const,
  reviewNotes: null,
  reviewedAt: null,
  reportedCountry: "FR",
  createdAt: "2026-01-01T00:00:00.000Z",
  updatedAt: "2026-01-01T00:00:00.000Z",
};

const MOCK_ARENA_TOURNAMENT_ROUND = {
  position: 1,
  name: "Round 1",
  startsAt: new Date("2026-10-02T00:00:00.000Z"),
  endsAt: new Date("2026-10-06T00:00:00.000Z"),
  format: "WINNINGS" as const,
  qualifiersCount: 64,
  campaignName: null,
  isLast: false,
  status: "LIVE" as const,
  entrantsCount: 120,
  duelsPlayed: 480,
  closedAt: null,
};

const MOCK_ARENA_TOURNAMENT = {
  slug: "100k",
  name: "$100K Tournament",
  status: "LIVE" as const,
  requiresWhitelist: true,
  enrollsAutomatically: false,
  sniperMinDuels: 15,
  startsAt: new Date("2026-10-02T00:00:00.000Z"),
  endsAt: new Date("2026-10-08T01:00:00.000Z"),
  joinClosesAt: new Date("2026-10-06T00:00:00.000Z"),
  payoutAt: new Date("2026-10-08T03:00:00.000Z"),
  paidAt: null,
  participantsCount: 120,
  currentRoundPosition: 1,
  rounds: [MOCK_ARENA_TOURNAMENT_ROUND],
  pool: {
    volumeUsdt: "4800",
    reachedTierIndex: 0,
    currentPoolUsdt: "4000",
    ladder: [
      {
        thresholdUsdt: "0",
        poolUsdt: "4000",
        places: [
          { rankFrom: 1, rankTo: 1, amountUsdt: "2000" },
          { rankFrom: 2, rankTo: 2, amountUsdt: "1000" },
          { rankFrom: 3, rankTo: 3, amountUsdt: "400" },
        ],
        sniperUsdt: "300",
        streakUsdt: "300",
      },
    ],
  },
};

const MOCK_ARENA_TOURNAMENT_ROUND_LEADERBOARD = {
  round: MOCK_ARENA_TOURNAMENT_ROUND,
  items: [],
  total: 0,
  page: 0,
  size: 50,
  frozen: false,
};

const MOCK_ARENA_TOURNAMENT_ME = {
  joined: false,
  joinedAt: null,
  joinBlocker: null,
  disqualified: false,
  rounds: [],
  sniper: { rank: null, winRate: "0", wins: 0, played: 0, duelsToEligibility: 15 },
  streak: { rank: null, longestStreak: 0, behindLeader: 0 },
};

const MOCK_CMS_ARENA_TOURNAMENT = {
  id: 1,
  slug: "100k",
  name: "$100K Tournament",
  requiresWhitelist: true,
  sniperMinDuels: 15,
  payoutGraceMinutes: 120,
  tournament: { ...MOCK_ARENA_TOURNAMENT, rounds: [{ ...MOCK_ARENA_TOURNAMENT_ROUND, frozenAt: null }] },
  payouts: [],
  undistributedUsdt: null,
};

const MOCK_BIG_ARENA_COMPETITION_REGISTRATION = {
  id: 1,
  hubUserId: 1,
  competition: "BIG_100K" as const,
  email: "trader@example.com",
  defiAddress: null,
  xHandle: "trader",
  telegramHandle: null,
  status: "PENDING" as const,
  acceptedVia: null,
  priorityCode: null,
  utm: null,
  referralUtm: "bac_mock1234",
  referralCount: 0,
  sameSocialAs: [],
  reviewedAt: null,
  createdAt: "2026-01-01T00:00:00.000Z",
  updatedAt: "2026-01-01T00:00:00.000Z",
};

const MOCK_CMS_ARENA_CLAN = {
  id: 1,
  slug: "night-shift",
  name: "Night Shift",
  logoUrl: null,
  isPrivate: false,
  password: null,
  maxMembers: 50,
  isActive: true,
  memberCount: 0,
  chief: null,
  createdAt: new Date("2026-09-01T00:00:00Z"),
  updatedAt: new Date("2026-09-01T00:00:00Z"),
};

const MOCK_CMS_ARENA_CLAN_TOURNAMENT = {
  id: 1,
  clanId: 1,
  clanName: "Night Shift",
  name: "Night Shift Cup",
  startsAt: new Date("2026-09-14T00:00:00Z"),
  endsAt: new Date("2026-09-16T00:00:00Z"),
  rankingMode: "WINNINGS" as const,
  rewardBreakdown: [{ rankFrom: 1, rankTo: 1, rewardUsdt: "120" }],
  status: "UPCOMING" as const,
  settledAt: null,
  createdAt: new Date("2026-09-01T00:00:00Z"),
  updatedAt: new Date("2026-09-01T00:00:00Z"),
  standings: [],
};

const MOCK_BIG_ARENA_COMPETITION_PRIORITY_CODE = {
  id: 1,
  code: "BAC-MOCK-2345",
  label: "Mock KOL",
  createdAt: "2026-01-01T00:00:00.000Z",
  usedAt: null,
  usedBy: null,
  disabledAt: null,
};

const MOCK_CMS_ARENA_TICKET_LINK = {
  id: "00000000-0000-4000-8000-000000000000",
  slug: "mock-partner",
  partnerName: "Mock Partner",
  headline: null,
  enabled: true,
  maxClaims: 1,
  claimCount: 0,
  items: [{ faceValueUsdt: "25", quantity: 3, expiryDurationMs: 86_400_000, sortOrder: 0 }],
  stats: {
    ticketsGranted: 0,
    ticketsActive: 0,
    ticketsUsed: 0,
    ticketsExpired: 0,
    totalFaceValueGrantedUsdt: "0",
    totalFaceValueUsedUsdt: "0",
  },
  createdAt: "2026-01-01T00:00:00.000Z",
  updatedAt: "2026-01-01T00:00:00.000Z",
};

const MOCK_PRIVATE_COMPETITION_ROOM = {
  slug: "mock-private-room",
  status: "WAITING" as const,
  ownerHubUserId: 1,
  config: {
    format: "CLASSIC" as const,
    durationSeconds: 600,
    entryFeesUsdt: "10",
    minVolumeUsd: "0",
    initialDepositUsd: 10_000,
    pairSymbols: ["BTC/USDT:USDT"],
    creatorRakeBps: 0,
  },
  rakeBps: 0,
  participants: [{ hubUserId: 1, seat: 0, nickname: null, isOwner: true }],
  campaignName: null,
  startsAt: null,
  endsAt: null,
  viewerHubUserId: 1,
  isOwner: true,
  isParticipant: true,
  canStart: false,
  isSpinAndGo: false,
  spinAndGoPotFactor: null,
};

export const API_MOCK_DATA: APIResponseMap = {
  [API_ENDPOINT.EXCHANGES_STATUS]: [
    {
      id: CEDE_CEXS.COINBASE,
      name: "Coinbase",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.BINANCE,
      name: "Binance",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.KRAKEN,
      name: "Kraken",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.GATEIO,
      name: "Gate.io",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.BITFINEX,
      name: "Bitfinex",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.HTX,
      name: "HTX",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.KUCOIN,
      name: "KuCoin",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.BITSTAMP,
      name: "Bitstamp",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.OKX,
      name: "OKX",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.BITGET,
      name: "Bitget",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.BITMART,
      name: "BitMart",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.BYBIT,
      name: "Bybit",
      status: ExchangeStatus.Ok,
    },
    {
      id: CEDE_CEXS.MEXC,
      name: "MEXC",
      status: ExchangeStatus.Ok,
    },
  ],
  [API_ENDPOINT.FIAT_CURRENCIES]: [
    {
      symbol: "USD",
      name: "United States Dollar",
      symbolNative: "$",
    },
    {
      symbol: "EUR",
      name: "Euro",
      symbolNative: "€",
    },
    {
      symbol: "GBP",
      name: "British Pound Sterling",
      symbolNative: "£",
    },
  ],
  [API_ENDPOINT.STANDARDIZED_CHAINS]: {
    binance: [
      {
        ecid: "ETH",
        icid: "ethereum",
        icn: "Ethereum",
        chainId: 1,
      },
      {
        ecid: "DOT",
        icid: "polkadot",
        icn: "Polkadot",
        chainId: 2,
      },
      {
        ecid: "AVAX",
        icid: "avalanche",
        icn: "Avalanche",
        chainId: 3,
      },
    ],
    coinbase: [
      {
        ecid: "ethereum",
        icid: "ethereum",
        icn: "Ethereum",
        chainId: 1,
      },
    ],
    okx: [
      {
        ecid: "ERC20",
        icid: "ethereum",
        icn: "Ethereum",
        chainId: 1,
      },
    ],
  },
  [API_ENDPOINT.PRICES]: {
    data: {
      updatedAt: "2021-09-20T08:51:12.000Z",
      tokens: [
        {
          tokenSymbol: "USDC",
          average: 1,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 1,
            [CEDE_CEXS.COINBASE]: 1,
            [CEDE_CEXS.BITGET]: 1,
            [CEDE_CEXS.KUCOIN]: 1,
            [CEDE_CEXS.OKX]: 1,
            [CEDE_CEXS.BITFINEX]: 1,
            [CEDE_CEXS.BYBIT]: 1,
            [CEDE_CEXS.HTX]: 1,
            [CEDE_CEXS.KRAKEN]: 1,
            [CEDE_CEXS.GATEIO]: 1,
            [CEDE_CEXS.BITMART]: 1,
            [CEDE_CEXS.BINANCE]: 1,
          },
        },
        {
          tokenSymbol: "BTC",
          average: 26854.01620548817,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 26859.833,
            [CEDE_CEXS.COINBASE]: 26840.316203576476,
            [CEDE_CEXS.BITGET]: 26856.019189999995,
            [CEDE_CEXS.KUCOIN]: 26985.197453932367,
            [CEDE_CEXS.OKX]: 26790.1762308,
            [CEDE_CEXS.BITFINEX]: 26857.831,
            [CEDE_CEXS.BYBIT]: 26829.1276505092,
            [CEDE_CEXS.HTX]: 26855.298469999998,
            [CEDE_CEXS.KRAKEN]: 26840.1,
            [CEDE_CEXS.GATEIO]: 26857.630799999995,
            [CEDE_CEXS.BITMART]: 26835.50246841,
            [CEDE_CEXS.BINANCE]: 26841.16199863,
          },
        },
        {
          tokenSymbol: "ETH",
          average: 1638.3047911131491,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 1638.5368999999998,
            [CEDE_CEXS.COINBASE]: 1637.67902,
            [CEDE_CEXS.BITGET]: 1637.74500528,
            [CEDE_CEXS.KUCOIN]: 1640.448408955305,
            [CEDE_CEXS.OKX]: 1637.67902,
            [CEDE_CEXS.BITFINEX]: 1638.8372,
            [CEDE_CEXS.BYBIT]: 1637.7762899224856,
            [CEDE_CEXS.HTX]: 1639.2075699999998,
            [CEDE_CEXS.KRAKEN]: 1637.88,
            [CEDE_CEXS.GATEIO]: 1639.1274899999999,
            [CEDE_CEXS.BITMART]: 1637.27651,
            [CEDE_CEXS.BINANCE]: 1637.4640791999998,
          },
        },
        {
          tokenSymbol: "AVAX",
          average: 9.455109874685059,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 9.473832728420724,
            [CEDE_CEXS.GATEIO]: 9.448,
            [CEDE_CEXS.BYBIT]: 9.4598918778,
            [CEDE_CEXS.BITFINEX]: 9.453,
            [CEDE_CEXS.HTX]: 9.4524,
            [CEDE_CEXS.OKX]: 9.445359,
            [CEDE_CEXS.KRAKEN]: 9.46,
            [CEDE_CEXS.BITGET]: 9.4516,
            [CEDE_CEXS.KUCOIN]: 9.444792210000001,
            [CEDE_CEXS.COINBASE]: 9.454222679999999,
            [CEDE_CEXS.BINANCE]: 9.46242,
            [CEDE_CEXS.BITMART]: 9.4558,
          },
        },
        {
          tokenSymbol: "USDT",
          average: 0.995892009896678,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 0.9994891104662439,
            [CEDE_CEXS.GATEIO]: 0.998761239,
            [CEDE_CEXS.BYBIT]: 1.0007220978867657,
            [CEDE_CEXS.BITFINEX]: 1.0004608327,
            [CEDE_CEXS.OKX]: 0.999978,
            [CEDE_CEXS.KRAKEN]: 0.9999809474200001,
            [CEDE_CEXS.BITGET]: 0.9996880566905006,
            [CEDE_CEXS.KUCOIN]: 0.9613928192032687,
            [CEDE_CEXS.COINBASE]: 1.0001779956,
            [CEDE_CEXS.BINANCE]: 0.998269,
          },
        },
        {
          tokenSymbol: "LINK",
          average: 7.521685779649785,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 7.5147387,
            [CEDE_CEXS.GATEIO]: 7.5317,
            [CEDE_CEXS.BYBIT]: 7.5281343774,
            [CEDE_CEXS.BITFINEX]: 7.5375,
            [CEDE_CEXS.HTX]: 7.5327,
            [CEDE_CEXS.OKX]: 7.5068532,
            [CEDE_CEXS.KRAKEN]: 7.513076760000001,
            [CEDE_CEXS.BITGET]: 7.511691809999999,
            [CEDE_CEXS.KUCOIN]: 7.521034533600001,
            [CEDE_CEXS.COINBASE]: 7.51917054,
            [CEDE_CEXS.BINANCE]: 7.510729434797414,
            [CEDE_CEXS.BITMART]: 7.5329,
          },
        },
        {
          tokenSymbol: "BNB",
          average: 212.78111366,
          exchanges: {
            [CEDE_CEXS.GATEIO]: 213,
            [CEDE_CEXS.BYBIT]: 212.8291,
            [CEDE_CEXS.HTX]: 213.04,
            [CEDE_CEXS.OKX]: 212.83531752000002,
            [CEDE_CEXS.BITGET]: 212.98,
            [CEDE_CEXS.KUCOIN]: 212.51532456,
            [CEDE_CEXS.BINANCE]: 212.4316432,
            [CEDE_CEXS.BITMART]: 212.617524,
          },
        },
        {
          tokenSymbol: "DOT",
          average: 3.771710283627618,
          exchanges: {
            [CEDE_CEXS.BITSTAMP]: 3.7801075875314116,
            [CEDE_CEXS.GATEIO]: 3.7784,
            [CEDE_CEXS.BYBIT]: 3.777916884,
            [CEDE_CEXS.BITFINEX]: 3.7676,
            [CEDE_CEXS.HTX]: 3.7736,
            [CEDE_CEXS.OKX]: 3.76762023,
            [CEDE_CEXS.KRAKEN]: 3.7692799200000002,
            [CEDE_CEXS.BITGET]: 3.7768,
            [CEDE_CEXS.KUCOIN]: 3.768917082,
            [CEDE_CEXS.COINBASE]: 3.7559843999999996,
            [CEDE_CEXS.BINANCE]: 3.7691973,
            [CEDE_CEXS.BITMART]: 3.7751,
          },
        },
      ],
    },
  },
  [API_ENDPOINT.SUPPORTED_TOKENS_BY_EXCHANGE]: [
    "AGLD",
    "STPT",
    "MXN",
    "MATICUSDCE",
    "UGX",
    "GLM",
    "RAY",
    "NEAR",
    "AUDIO",
    "WLD",
    "FDUSD",
    "FARM",
    "SDG",
    "XNO",
    "AVAOLD",
    "DGB",
    "ZRX",
    "SANTOS",
    "WING",
    "WNXM",
    "BCH",
    "AI",
    "JST",
    "HOT",
    "XOF",
    "GMT",
    "AR",
    "IRIS",
    "GMX",
    "SEI",
    "GNF",
    "QAR",
    "JTO",
    "FLM",
    "GNO",
    "VITE",
    "GNS",
    "VANRY",
    "CFX",
    "T",
    "LYD",
    "KWD",
    "SFP",
    "DIA",
    "ACA",
    "ISK",
    "ARDR",
    "ACE",
    "ACH",
    "BEL",
    "JUV",
    "ACM",
    "MINA",
    "TIA",
    "VTHO",
    "PAB",
    "WOO",
    "REI",
    "BLUR",
    "REN",
    "ADA",
    "ELF",
    "REQ",
    "STORJ",
    "DF",
    "RARE",
    "PAXG",
    "PAX",
    "CHR",
    "VND",
    "WAVES",
    "CHZ",
    "ADX",
    "XRP",
    "PGALA",
    "FLOKI",
    "JASMY",
    "TJS",
    "AED",
    "FIDA",
    "SAND",
    "OCEAN",
    "FOR",
    "UMA",
    "SCRT",
    "TUSD",
    "TKO",
    "IDRT",
    "BHD",
    "ENJ",
    "KZT",
    "OAX",
    "GRT",
    "AFN",
    "HARD",
    "TFUEL",
    "ENS",
    "TROY",
    "UNI",
    "TLM",
    "HUF",
    "CKB",
    "WRX",
    "LUNC",
    "XTZ",
    "LUNA",
    "FUSDC",
    "BCHA",
    "EOS",
    "RIF",
    "SKL",
    "GTC",
    "PEN",
    "SOLO",
    "TMT",
    "BURGER",
    "HOOK",
    "XVG",
    "EPS",
    "TND",
    "CLV",
    "EPX",
    "FLOW",
    "XVS",
    "MEME",
    "STEEM",
    "SLP",
    "VRT",
    "ID",
    "DOP",
    "LAZIO",
    "DOT",
    "IQ",
    "1INCH",
    "CHESS",
    "MAD",
    "DEGO",
    "GYEN",
    "UNFI",
    "FTM",
    "POWR",
    "ERN",
    "MAV",
    "FTT",
    "VOXEL",
    "PHA",
    "RLC",
    "BEAMX",
    "PHB",
    "ARKM",
    "ATOM",
    "PENDLE",
    "QUICK",
    "BLZ",
    "MBL",
    "SNT",
    "PHP",
    "SNX",
    "FUN",
    "COP",
    "COS",
    "API3",
    "QKC",
    "ROSE",
    "GLMR",
    "SOL",
    "ETB",
    "CITY",
    "ETC",
    "BNB",
    "CELR",
    "OGN",
    "LAK",
    "ETH",
    "BND",
    "NEO",
    "CELO",
    "KLAY",
    "AUCTION",
    "TUSDOLD",
    "BADGER",
    "MULTI",
    "HIGH",
    "TRB",
    "BNT",
    "LBA",
    "BNX",
    "UTK",
    "ORDI",
    "WSOL",
    "MDL",
    "AMB",
    "TRU",
    "WBNB",
    "AMD",
    "DREP",
    "NFP",
    "TRY",
    "TRX",
    "MDT",
    "NFT",
    "MDX",
    "AERGO",
    "EUR",
    "AMP",
    "HIFI",
    "NULS",
    "USTC",
    "RON",
    "RDNTOLD",
    "NGN",
    "CRC",
    "PKR",
    "EGLD",
    "PUNDIX",
    "SPELL",
    "FXS",
    "PLA",
    "EVX",
    "CRV",
    "BAKE",
    "ANT",
    "FLUX",
    "RPL",
    "TORN",
    "PLN",
    "MAGIC",
    "LDO",
    "QNT",
    "ALICE",
    "OG",
    "ETHW",
    "OM",
    "AEUR",
    "OP",
    "BTTOLD",
    "BETH",
    "WETH",
    "BETA",
    "APE",
    "SSV",
    "BUSD",
    "CTK",
    "ARPA",
    "BRL",
    "ALCX",
    "MATIC",
    "APT",
    "STG",
    "IOTX",
    "SHIB",
    "FRONT",
    "KDA",
    "ZAR",
    "QUICKOLD",
    "DOCK",
    "STX",
    "PNT",
    "QI",
    "DENT",
    "MBOX",
    "TWD",
    "IOST",
    "SUI",
    "CAKE",
    "BSW",
    "WBETH",
    "OMG",
    "BAND",
    "SUN",
    "ASTR",
    "TRXOLD",
    "BTC",
    "TWT",
    "NKN",
    "RSR",
    "UYU",
    "ARB",
    "IOTA",
    "CVC",
    "REEF",
    "OMR",
    "KES",
    "ARK",
    "LOKA",
    "CVP",
    "KEY",
    "ARS",
    "CVX",
    "ONE",
    "ONG",
    "ANKR",
    "SUSHI",
    "ALGO",
    "UZS",
    "SC",
    "WBTC",
    "ONT",
    "BTTC",
    "RUB",
    "PIVX",
    "ASR",
    "AST",
    "MANA",
    "ATA",
    "DZD",
    "NMR",
    "MKR",
    "DODO",
    "LIT",
    "KGS",
    "ICP",
    "ATM",
    "JEX",
    "XAF",
    "ICX",
    "POLYX",
    "XAI",
    "LOOM",
    "KP3R",
    "DOGE",
    "DUSK",
    "ALPHA",
    "HBAR",
    "SXP",
    "RVN",
    "TZS",
    "MLN",
    "PEPE",
    "AUD",
    "KHR",
    "CTSI",
    "BWP",
    "KAVA",
    "C98",
    "OSMO",
    "PSG",
    "SYN",
    "MMK",
    "VIDT",
    "NOK",
    "AVA",
    "SYS",
    "STRAX",
    "GAL",
    "CZK",
    "GAS",
    "COMBO",
    "THETA",
    "WAN",
    "ORN",
    "PERL",
    "NEXO",
    "MASK",
    "AAVE",
    "MNT",
    "GBP",
    "BONK",
    "PERP",
    "BYN",
    "BOND",
    "YFI",
    "LQTY",
    "BICO",
    "XEC",
    "YGG",
    "PEOPLE",
    "AXS",
    "ZIL",
    "VAI",
    "XEM",
    "CTXC",
    "BIDR",
    "COMP",
    "HFT",
    "OOKI",
    "RUNE",
    "FORTH",
    "KMD",
    "GHST",
    "ALPINE",
    "LEVER",
    "IDEX",
    "GEL",
    "DEXE",
    "AVAX",
    "AZN",
    "UAH",
    "KNC",
    "PROS",
    "PROM",
    "BGBP",
    "LPT",
    "HIVE",
    "GFT",
    "BIFI",
    "RDNT",
    "PORTO",
    "OPUSDCE",
    "PYR",
    "WAXP",
    "DAI",
    "DAR",
    "FET",
    "LRC",
    "VET",
    "MTL",
    "ALPACA",
    "USDT",
    "OXT",
    "USDP",
    "DASH",
    "ILS",
    "ILV",
    "GHS",
    "EDU",
    "JOD",
    "HKD",
    "JOE",
    "CAN",
    "DCR",
    "CREAM",
    "DATA",
    "IMX",
    "USDC",
    "LTC",
    "WIN",
    "INJ",
    "TCT",
    "SAR",
    "LTO",
    "VGX",
    "DYDX",
    "EFI",
    "AGIX",
    "POND",
    "LINA",
    "1000SATS",
    "XLM",
    "LINK",
    "QTUM",
    "CYBER",
    "UFT",
    "SUPER",
    "POLS",
    "KSM",
    "ARBUSDCE",
    "FIL",
    "STMX",
    "RNDR",
    "BAL",
    "FIO",
    "GALA",
    "VIB",
    "VIC",
    "FIS",
    "BAR",
    "RAD",
    "BAT",
    "AKRO",
    "NZD",
    "MOVR",
    "COTI",
  ],
  [API_ENDPOINT.SUPPORTED_NETWORKS_BY_EXCHANGE]: [
    {
      name: "Ethereum",
      network: "ethereum",
      withdrawFee: "335",
      withdrawMax: "10000000000",
      withdrawMin: "670",
      depositEnabled: true,
      withdrawalEnabled: true,
      chainId: 1,
      iconUrl: forgeNetworkURL("ethereum"),
    },
    {
      name: "Polygon",
      network: "polygon",
      withdrawFee: "1",
      withdrawMax: "100000000",
      withdrawMin: "10",
      depositEnabled: true,
      withdrawalEnabled: true,
      chainId: 137,
      iconUrl: forgeNetworkURL("polygon"),
    },
    {
      name: "Solana",
      network: "solana",
      withdrawFee: "1",
      withdrawMax: "250000000",
      withdrawMin: "10",
      depositEnabled: true,
      withdrawalEnabled: true,
      chainId: 1399811149,
      iconUrl: forgeNetworkURL("solana"),
    },
    {
      name: "BNB Smart Chain",
      network: "bsc",
      withdrawFee: "6.28",
      withdrawMax: "10000000000",
      withdrawMin: "12",
      depositEnabled: true,
      withdrawalEnabled: true,
      chainId: 56,
      iconUrl: forgeNetworkURL("bsc"),
    },
    {
      name: "Near Protocol",
      network: "near",
      withdrawFee: "1",
      withdrawMax: "9999999",
      withdrawMin: "10",
      depositEnabled: true,
      withdrawalEnabled: true,
      iconUrl: forgeNetworkURL("near"),
    },
    {
      name: "BNB Beacon Chain",
      network: "bnb",
      withdrawFee: "2.61",
      withdrawMax: "10000000000",
      withdrawMin: "5.22",
      depositEnabled: true,
      withdrawalEnabled: true,
      iconUrl: forgeNetworkURL("bnb"),
    },
  ],
  [API_ENDPOINT.ALL_FIAT_CURRENCIES]: EXISTING_FIAT_CURRENCIES,
  [API_ENDPOINT.TRADE_PATH]: [
    {
      from: "NEAR",
      to: "BNB",
      orderSide: "sell",
      pairSymbol: "NEAR/BNB",
      precision: {
        amount: 1e-7,
        price: 1e-7,
      },
    },
    {
      from: "BNB",
      to: "SUI",
      orderSide: "sell",
      pairSymbol: "BNB/SUI",
      precision: {
        amount: 1e-7,
        price: 1e-7,
      },
    },
  ],
  [API_ENDPOINT.KRAKEN_WITHDRAWAL_NETWORKS_INFO]: [
    {
      asset: "ZUSD",
      method: "Bank Frick (SWIFT) Europe",
      method_id: "bank_frick_swift_europe",
      network: "",
      network_id: "",
      minimum: "100.00",
    },
    {
      asset: "ZEUR",
      method: "Bank Frick (SWIFT) Europe",
      method_id: "bank_frick_swift_europe",
      network: "",
      network_id: "",
      minimum: "100.00",
    },
    {
      asset: "ZEUR",
      method: "Banking Circle (SEPA & SEPA Instant)",
      method_id: "banking_circle_sepa",
      network: "",
      network_id: "",
      minimum: "2.00",
    },
    {
      asset: "ZGBP",
      method: "Bank Frick (SWIFT) Europe",
      method_id: "bank_frick_swift_europe",
      network: "",
      network_id: "",
      minimum: "100.00",
    },
    {
      asset: "ZCAD",
      method: "Bank Frick (SWIFT) Europe",
      method_id: "bank_frick_swift_europe",
      network: "",
      network_id: "",
      minimum: "100.00",
    },
    {
      asset: "ZJPY",
      method: "Bank Frick (SWIFT) Europe",
      method_id: "bank_frick_swift_europe",
      network: "",
      network_id: "",
      minimum: "15000.00",
    },
    {
      asset: "CHF",
      method: "Bank Frick (SWIFT) Europe",
      method_id: "bank_frick_swift_europe",
      network: "",
      network_id: "",
      minimum: "100.00",
    },
    {
      asset: "XXBT",
      method: "Bitcoin",
      method_id: "bitcoin",
      network: "Bitcoin",
      network_id: "bitcoin",
      minimum: "0.0004",
    },
    {
      asset: "XETH",
      method: "Ether",
      method_id: "ethereum",
      network: "Ethereum",
      network_id: "ethereum",
      minimum: "0.005",
    },
    {
      asset: "XETH",
      method: "Arbitrum One",
      method_id: "arbitrum_one",
      network: "Arbitrum One",
      network_id: "arbitrum_one",
      minimum: "0.0020",
    },
    {
      asset: "XETH",
      method: "Ethereum (Polygon)",
      method_id: "ethereum_polygon",
      network: "Polygon",
      network_id: "polygon",
      minimum: "0.0020",
    },
    {
      asset: "XETH",
      method: "Arbitrum Nova",
      method_id: "arbitrum_nova",
      network: "Arbitrum Nova",
      network_id: "arbitrum_nova",
      minimum: "0.0020",
    },
    {
      asset: "XETH",
      method: "Optimism",
      method_id: "optimism",
      network: "Optimism",
      network_id: "optimism",
      minimum: "0.0020",
    },
    {
      asset: "XETH",
      method: "zkSync Era",
      method_id: "zksync_era",
      network: "zkSync Era",
      network_id: "zksync_era",
      minimum: "0.0031",
    },
    {
      asset: "XETC",
      method: "Ether Classic",
      method_id: "ethereum_classic",
      network: "Ethereum Classic",
      network_id: "ethereum_classic",
      minimum: "0.01",
    },
    {
      asset: "USDT",
      method: "Tether USD (ERC20)",
      method_id: "usdt_erc20",
      network: "Ethereum",
      network_id: "ethereum",
      minimum: "16.00",
    },
    {
      asset: "USDT",
      method: "Tether USD (TRC20)",
      method_id: "usdt_trc20",
      network: "Tron",
      network_id: "tron",
      minimum: "6.00",
    },
    {
      asset: "USDT",
      method: "Tether USD (SPL)",
      method_id: "usdt_spl",
      network: "Solana",
      network_id: "solana",
      minimum: "2.00",
    },
    {
      asset: "USDT",
      method: "Tether USD (Polygon)",
      method_id: "usdt_polygon",
      network: "Polygon",
      network_id: "polygon",
      minimum: "2.00",
    },
    {
      asset: "USDT",
      method: "Arbitrum One Network",
      method_id: "usdt_arbitrum",
      network: "Arbitrum One",
      network_id: "arbitrum_one",
      minimum: "4.00",
    },
    {
      asset: "USDT",
      method: "Optimism",
      method_id: "usdt_optimism",
      network: "Optimism",
      network_id: "optimism",
      minimum: "2.50",
    },
    {
      asset: "LINK",
      method: "Link",
      method_id: "link",
      network: "Ethereum",
      network_id: "ethereum",
      minimum: "1.50",
    },
    {
      asset: "DOT",
      method: "Polkadot",
      method_id: "polkadot",
      network: "Polkadot",
      network_id: "polkadot",
      minimum: "1.05",
    },
    {
      asset: "AVAX",
      method: "Avalanche C-Chain",
      method_id: "avalanche_c",
      network: "Avalanche C-Chain",
      network_id: "avalanche_c",
      minimum: "0.5",
    },
  ],
  [API_ENDPOINT.TRADE_PATH_BY_SUPPORTED_TOKENS]: {
    USDT: [
      {
        from: "ALGO",
        to: "USDT",
        orderSide: "sell",
        pairSymbol: "ALGO/USDT",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    USDC: [
      {
        from: "ALGO",
        to: "USDC",
        orderSide: "sell",
        pairSymbol: "ALGO/USDC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    KNC: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "KNC",
        orderSide: "buy",
        pairSymbol: "KNC/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    DATA: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "DATA",
        orderSide: "buy",
        pairSymbol: "DATA/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    MATIC: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "MATIC",
        orderSide: "buy",
        pairSymbol: "MATIC/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    DAI: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "DAI",
        orderSide: "sell",
        pairSymbol: "BTC/DAI",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    SAND: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "SAND",
        orderSide: "buy",
        pairSymbol: "SAND/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    GRT: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "GRT",
        orderSide: "buy",
        pairSymbol: "GRT/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    QUICK: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "QUICK",
        orderSide: "buy",
        pairSymbol: "QUICK/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    PYR: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "PYR",
        orderSide: "buy",
        pairSymbol: "PYR/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    VOXEL: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "VOXEL",
        orderSide: "buy",
        pairSymbol: "VOXEL/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    GMT: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "GMT",
        orderSide: "buy",
        pairSymbol: "GMT/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    NEXO: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "NEXO",
        orderSide: "buy",
        pairSymbol: "NEXO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    STG: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "STG",
        orderSide: "buy",
        pairSymbol: "STG/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    SYN: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "SYN",
        orderSide: "buy",
        pairSymbol: "SYN/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    VANRY: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "VANRY",
        orderSide: "buy",
        pairSymbol: "VANRY/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
    AXL: [
      {
        from: "ALGO",
        to: "BTC",
        orderSide: "sell",
        pairSymbol: "ALGO/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
      {
        from: "BTC",
        to: "AXL",
        orderSide: "buy",
        pairSymbol: "AXL/BTC",
        precision: {
          amount: 1e-7,
          price: 1e-7,
        },
      },
    ],
  },
  [API_ENDPOINT.TOKENS_MAPPING]: [],
  [API_ENDPOINT.TOKENS_METADATA]: {},
  [API_ENDPOINT.CAMPAIGN_OPEN_ORDERS]: [],
  [API_ENDPOINT.HUB_STATS]: {
    totalVolumeTraded: new Decimal(56986875.01883306),
    totalNumberOfTrades: 152715,
    totalRewardsReceived: new Decimal(161584.5999999976),
    completedCampaigns: 2,
    liveCampaigns: 2,
  },
  [API_ENDPOINT.USER_STATS]: {
    totalVolumeTraded: new Decimal(2753251.433132099),
    totalNumberOfTrades: 6004,
    totalRewardsReceived: new Decimal(6949.129999999998),
    campaignsParticipatingList: ["neon_2", "seda"],
  },
  [API_ENDPOINT.HUB_GLOBAL_LEADERBOARD]: [
    {
      defiAddress: "0xe6876Aa0282C3D050bf131C8f0Bb93330d256CAe",
      totalVolume: new Decimal(11450404.488217168),
      rank: 1,
    },
    {
      defiAddress: "0x167dAA01aC96Ccf1D9aCE255cd7b20bc4A860895",
      totalVolume: new Decimal(7897119.457897761),
      rank: 2,
    },
    {
      defiAddress: "0xf8575829fD42050997fB08093Db564037478be75",
      totalVolume: new Decimal(5385984.153300833),
      rank: 3,
    },
    {
      defiAddress: "0xD8b1d18707c1886B8db18a599f1383B32f8658EA",
      totalVolume: new Decimal(2753251.4331320836),
      rank: 4,
    },
    {
      defiAddress: "0x3D3eDC1865346bE5f3d92882332D0b73dc89d4ac",
      totalVolume: new Decimal(2642524.220280274),
      rank: 5,
    },
    {
      defiAddress: "0xb1ec72c4D4782053fc9ebEDb02838CEA0d8664D2",
      totalVolume: new Decimal(2204819.4136017733),
      rank: 6,
    },
    {
      defiAddress: "0x1aa3CFfa556aEe20C08Ce6112a35Eb94f9fF325A",
      totalVolume: new Decimal(1982963.409645091),
      rank: 7,
    },
    {
      defiAddress: "0x23e7472fC657Af6F9d4f9D6aeaD068B14a9D4327",
      totalVolume: new Decimal(1817895.0173047222),
      rank: 8,
    },
    {
      defiAddress: "0x6692b4eA56c872e1A511bB23d460539F6b3E71B8",
      totalVolume: new Decimal(1397026.8520959024),
      rank: 9,
    },
    {
      defiAddress: "0x21c11Fe94D7C9b82F1F18b6c8D16dAAedA2c6d70",
      totalVolume: new Decimal(1395567.420154534),
      rank: 10,
    },
  ],
  [API_ENDPOINT.LEADERBOARD]: {} as TradeToEarnLeaderboardDto,
  [API_ENDPOINT.PNL_SNAPSHOTS]: { users: [] },
  [API_ENDPOINT.PNL_PRICE_EVENTS]: { priceEvents: [] },
  [API_ENDPOINT.USER_PNL_STATS]: { userCount: 0, rank: null, snapshots: [] },
  [API_ENDPOINT.ARENA_OPPONENT_ACTIVITY]: { players: [] },
  [API_ENDPOINT.HUB_TRADE_PERPS_PNL_OPT_IN]: { success: true },
  [API_ENDPOINT.VALIDATE_DEFI_ADDRESS]: {
    isValid: true,
    defiAddresses: ["0x0000000000000000000000000000000000000000"],
    reason: "max_defi_addresses_by_exchange_user_id",
  },

  [API_ENDPOINT.HUB_USER_ME]: {
    id: 32,
    defiAddress: "0x9649471D2fBBb547A3Bb0D364266F434cA1Bca72",
    createdAt: new Date("2024-12-13T14:43:59.272Z"),
    exchangeAccounts: [],
    socialAccounts: [],
    isBanned: false,
    bestSocial: null,
    nftBalances: [],
    automaticStreakCguAcknowledged: false,
  },
  [API_ENDPOINT.HUB_USER_SIGNIN]: {
    user: {
      id: 32,
      defiAddress: "0x9649471D2fBBb547A3Bb0D364266F434cA1Bca72",
      createdAt: new Date("2024-12-13T14:43:59.272Z"),
      isBanned: false,
      automaticStreakCguAcknowledged: false,
    },
    isNewUser: false,
  },
  [API_ENDPOINT.HUB_USER_LINK_WALLET]: {
    defiAddress: "0x9649471d2fbbb547a3bb0d364266f434ca1bca72",
  },
  [API_ENDPOINT.HUB_USER_LINK_EMAIL]: {
    email: "user@example.com",
  },
  [API_ENDPOINT.HUB_USER_AUTOMATIC_STREAK_CGU_ACKNOWLEDGMENT]: {
    success: true,
  },
  [API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_BALANCES]: {},
  [API_ENDPOINT.HUB_EXCHANGE_MARGIN_ASSET_MODE]: { assetMode: "single" },
  [API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_CHECK_CREDENTIALS]: { valid: true },
  [API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_POSITIONS]: {},
  [API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_FUNDING_HISTORY]: { fundingHistory: [], total: 0 },
  [API_ENDPOINT.HUB_DEPOSIT_ADDRESS]: {
    tokenSymbol: "USDT",
    address: "0x1234567890123456789012345678901234567890",
    network: "arbitrumone",
    tag: undefined,
  },
  [API_ENDPOINT.HUB_SUPPORTED_EXCHANGES]: [],
  [API_ENDPOINT.HUB_TRADE_PUBLIC_MARKET_RATE]: {
    bid: "100",
    ask: "100",
  },
  [API_ENDPOINT.HUB_TRADE_MARKET_PAIRS]: [],
  [API_ENDPOINT.ADMIN_HUB_TRADE_MARKET_PAIRS]: [],
  [API_ENDPOINT.HUB_TRADE_PREPARE_ORDER]: {
    estimatedFee: {
      amount: 0.005099999999787,
      tokenSymbol: "USDT",
      refAmount: 0.005109468512415619,
    },
    estimatedAmount: "0.007351948051641",
    createOrderRequest: {
      pairSymbol: "BNB/USDT",
      orderSide: "buy",
      orderType: OrderType.MARKET,
      amount: "0.007351948051641",
      price: "693",
    },
  },
  [API_ENDPOINT.HUB_TRADE_CREATE_ORDER]: {
    id: "order123",
    timestamp: Date.now(),
    status: OrderStatus.OPEN,
    pairSymbol: "BTC/USDT",
    type: OrderType.MARKET,
    side: "buy",
    price: new Decimal(45000),
    amount: new Decimal(0.1),
    filled: new Decimal(0.1),
    fee: {
      amount: new Decimal(0.0001),
      tokenSymbol: "BTC",
      refAmount: new Decimal(4.5),
    },
  },
  [API_ENDPOINT.HUB_TRADE_RETRIEVE_ORDER]: {
    id: "order123",
    timestamp: Date.now(),
    status: OrderStatus.OPEN,
    pairSymbol: "BTC/USDT",
    type: OrderType.MARKET,
    side: "buy",
    price: 45000,
    amount: 0.1,
    filled: 0.1,
    fee: {
      amount: 0.0001,
      tokenSymbol: "BTC",
      refAmount: 4.5,
    },
  },
  [API_ENDPOINT.HUB_TRADE_MAX_LEVERAGE]: { maxLeverage: 125 },
  [API_ENDPOINT.HUB_TRADE_OPEN_ORDERS]: {},
  [API_ENDPOINT.HUB_TRADE_OHLCV]: [],
  [API_ENDPOINT.HUB_CREATE_EXCHANGE_ACCOUNT]: {
    id: 1,
    name: "Binance",
    exchangeId: "binance",
    publicApiKey: "publicApiKey",
    status: "online",
    hubUserId: 32,
    accountId: "accountId",
  },
  [API_ENDPOINT.USER_PHASE_VOLUME]: { userVolume: new Decimal(0) },
  [API_ENDPOINT.HUB_OAUTH_INIT]: { url: "https://app.cedehub.io/" },
  [API_ENDPOINT.HUB_GET_INTERNAL_CACHE]: { test: "test" },
  [API_ENDPOINT.HUB_GET_ACTIVE_CACHE_KEYS]: ["test", "test2"],
  [API_ENDPOINT.HUB_DELETE_FROM_CACHE]: { success: true },
  [API_ENDPOINT.HUB_DELETE_FROM_CACHE_BULK]: { success: true },
  [API_ENDPOINT.HUB_TRADE_CANCEL_ORDER]: { status: CancelOrderStatus.CANCELED },
  [API_ENDPOINT.HUB_REWARDS_CLAIMABLE]: {
    claimableEpochs: {
      claimablesData: [],
      allClaimsEncoded: "0x",
      batches: [],
    },
  },
  [API_ENDPOINT.HUB_REWARDS_CLAIMABLE_SOON]: {
    claimableSoonEpochs: [],
  },
  [API_ENDPOINT.HUB_REWARDS_MERKL_CLAIMABLE]: {
    campaigns: [],
    claimTransaction: null,
  },
  [API_ENDPOINT.HUB_SEND_EVENT]: { success: true },
  [API_ENDPOINT.HUB_SEND_EVENTS_BULK]: { success: true },
  [API_ENDPOINT.HUB_SET_PEOPLE]: { success: true },
  [API_ENDPOINT.HUB_TRADE_ESTIMATE_PRICE_FROM_ORDER_BOOK]: { estimatedPrice: 100 },
  [API_ENDPOINT.HUB_TRANSFER_ROUTES]: {},
  [API_ENDPOINT.HUB_TRANSFER_INTERNAL]: { id: "transfer-123" },
  [API_ENDPOINT.HUB_TRADE_ORDER_BOOK]: {
    asks: [[100, 1]],
    bids: [[99, 1]],
    timestamp: 0,
  },
  [API_ENDPOINT.HUB_TRADE_ORDER_BOOK_METRICS]: {
    spread: 100,
    midPrice: 100,
    depths: {
      2: {
        down: 100,
        up: 100,
      },
    },
  } satisfies OrderBookMetrics,
  [API_ENDPOINT.HUB_TRADE_ORDER_BOOK_DEPTH]: {
    up: 1000,
    down: 950,
  },
  [API_ENDPOINT.HUB_TRADE_TICKER]: {
    pairSymbol: "BTC/USDC:USDC",
    base: "BTC",
    quote: "USDC",
    price: 66000,
    markPrice: 66010,
  },
  [API_ENDPOINT.HUB_TRADE_MARK_PRICE]: {
    symbol: "BTC/USD:USD",
    markPrice: 66010,
    timestamp: Date.now(),
    datetime: new Date().toISOString(),
    info: {},
  },
  [API_ENDPOINT.HUB_TRADE_POPULAR_TOKENS]: {
    tokens: [],
    updatedAt: new Date().toISOString(),
  },
  [API_ENDPOINT.HUB_TRADE_MIN_AMOUNTS]: {
    minBaseAmount: "0.001",
    minQuoteAmount: "1",
    minPrice: "0",
  },
  [API_ENDPOINT.HUB_TRADE_VERIFY_ORDER]: { message: "Order verified" },
  [API_ENDPOINT.TON_CONNECT_VALIDATE_PROOF]: { id: "", accessToken: "", wallet: "" },
  [API_ENDPOINT.TON_CONNECT_GENERATE_PAYLOAD]: { payload: "" },
  [API_ENDPOINT.HUB_BAN_RECORDS]: [],
  [API_ENDPOINT.HUB_BANNED_USERS]: [],
  [API_ENDPOINT.ANALYSIS_HUB_USER_SUCCESS_RATE]: [],
  [API_ENDPOINT.ANALYSIS_EXCHANGE_USER_SUCCESS_RATE]: [],
  [API_ENDPOINT.ANALYSIS_CAMPAIGN_SUCCESS_RATE]: [],
  [API_ENDPOINT.ANALYSIS_IP_HASH_SUCCESS_RATE]: [],
  [API_ENDPOINT.HUB_USER_AFFECTED_BY_BAN_RECORD]: [],
  [API_ENDPOINT.EPOCHS_BY_CAMPAIGN]: {},
  [API_ENDPOINT.ANALYSIS_EPOCH_SUCCESS_RATE]: [],
  [API_ENDPOINT.HUB_BAN_RECORDS_USER_IP_HASHES]: [],
  [API_ENDPOINT.HUB_BAN_RECORDS_USER_EXCHANGE_ACCOUNTS]: [],
  [API_ENDPOINT.HUB_USER_VOLUME_IN_WINDOW]: {
    currentWindowVolume: new Decimal(0),
    nextWindowDate: undefined,
  },
  [API_ENDPOINT.HUB_REWARDS_SIMULATE]: { success: true },
  [API_ENDPOINT.HUB_GRAPH_OF_CONVERGING_ADDRESSES]: {
    id: 1,
    campaign: "campaign1",
    rewardVaultAddress: "0x123",
    depth: 1,
    graph: [],
    updatedAt: new Date(),
  },
  [API_ENDPOINT.HUB_CONVERGING_ADDRESSES_GRAPH_IDS_AND_CAMPAIGN]: [],
  [API_ENDPOINT.HUB_CONVERGING_ADDRESSES_UPDATE_GRAPHS]: { success: true },
  [API_ENDPOINT.HUB_CONVERGING_ADDRESSES_REMOVE_NODE_FROM_GRAPH]: {
    success: true,
    message: "Node removed from graph successfully",
  },
  [API_ENDPOINT.HUB_SOCIAL_OAUTH_INIT]: { url: "https://app.cedehub.io/" },
  [API_ENDPOINT.HUB_SOCIAL_OAUTH_REMOVE]: { success: true },
  [API_ENDPOINT.HUB_SOCIAL_OAUTH_SPECIAL_TELEGRAM_CALLBACK]: { success: true },
  [API_ENDPOINT.HUB_SOCIAL_OAUTH_LINK_TMA]: { success: true },
  [API_ENDPOINT.HUB_SKYWALKER_STATS]: {
    volume: [],
    volumeByEpoch: [],
    traders: 0,
    totalVolume: new Decimal(0),
    avg: new Decimal(0),
    ratio: new Decimal(0),
    medianVolume: new Decimal(0),
    medianOfAllTrades: new Decimal(0),
    totalRewards: new Decimal(0),
  },
  [API_ENDPOINT.HUB_SKYWALKER_EPOCH_HOURLY_VOLUME]: [
    {
      epochId: 1,
      campaignName: "campaign1",
      hourlyVolume: [],
    },
  ],
  [API_ENDPOINT.HUB_SKYWALKER_EPOCH_TRADERS]: {
    totals: { traders: 0, volumeUSD: new Decimal(0), tradeCount: 0, avgVolumePerTrader: new Decimal(0) },
    byEpoch: [],
  },
  [API_ENDPOINT.HUB_SKYWALKER_CAMPAIGN_USERS]: [],
  [API_ENDPOINT.HUB_SKYWALKER_USER_VOLUME]: {
    hubUserId: 1,
    defiAddress: null,
    total: { volumeUSD: new Decimal(0), tradeCount: 0 },
    byEpoch: [],
  },
  [API_ENDPOINT.HUB_SKYWALKER_USER_GEOGRAPHY]: {
    total: [],
    byEpoch: [],
  },
  [API_ENDPOINT.HUB_SKYWALKER_GLOBAL_USER_GEOGRAPHY]: {
    global: [],
    campaigns: [],
  },
  [API_ENDPOINT.HUB_SKYWALKER_HTE_CAMPAIGNS]: [],
  [API_ENDPOINT.HUB_SKYWALKER_HTE_STATS]: {
    campaign: { id: "campaign1", name: "campaign1", tokenSymbol: "TKN", startAt: new Date(), endAt: new Date() },
    kpis: {
      uniqueHolders: 0,
      epochsTotal: 0,
      epochsRemaining: 0,
      hasEpochInProgress: false,
      priceUnavailableEpochId: null,
      lastEpochId: null,
      peakHeldEpochId: null,
    },
    epochs: [],
  },
  [API_ENDPOINT.HUB_SKYWALKER_HTE_INTERNAL]: {
    epochId: null,
    epochsTotal: 0,
    rewardTokenSymbol: "USDT",
    poolUnlock: null,
    topHolders: [],
  },
  [API_ENDPOINT.HUB_REFERRAL_CODE]: {
    referralCode: "CEDETEST",
    id: 1,
    hubUserId: 1,
    expiresAt: new Date(),
    referralSettingsOverride: null,
  },
  [API_ENDPOINT.HUB_REFERRAL_ACCEPT]: {
    referrerId: 1,
    refereeId: 2,
    createdAt: new Date(),
    activatedAt: new Date(),
    expiredAt: null,
    referralCodeId: 1,
    status: REFERRAL_STATUS.ACTIVE,
    id: 1,
  },
  [API_ENDPOINT.HUB_REFERRAL_ELIGIBILITY]: {
    exchangeAccountCount: {
      target: 1,
      current: 1,
    },
    tradeVolumeUSD: {
      target: new Decimal(1),
      current: new Decimal(1),
    },
    eligibleOrderCount: {
      target: 1,
      current: 1,
    },
  },
  [API_ENDPOINT.HUB_REFERRALS]: {
    referralsAsReferrer: [
      {
        id: 101,
        referrerId: 1,
        refereeId: 1001,
        referralCodeId: 1,
        status: REFERRAL_STATUS.ACTIVE,
        createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        activatedAt: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000),
        expiredAt: null,
        referee: { id: 1001, defiAddress: "0xaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa" },
        referralEpochReward: [],
        totalVolumeUSD: new Decimal(0),
        totalRewardTokenAmount: "0",
        breakdownByExchange: {
          hl: { volumeUSD: "0", rewardTokenAmount: "0", previewVolumeUSD: "600" },
          pdx: { volumeUSD: "0", rewardTokenAmount: "0", previewVolumeUSD: "300" },
        },
        bonusExpiresAt: null,
        previewVolumeUSD: 900,
      },
      {
        id: 102,
        referrerId: 1,
        refereeId: 1002,
        referralCodeId: 1,
        status: REFERRAL_STATUS.ACTIVE,
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
        activatedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
        expiredAt: null,
        referee: { id: 1002, defiAddress: "0xbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb" },
        referralEpochReward: [],
        totalVolumeUSD: new Decimal(0),
        totalRewardTokenAmount: "0",
        breakdownByExchange: {
          hl: { volumeUSD: "0", rewardTokenAmount: "0", previewVolumeUSD: "334.56" },
        },
        bonusExpiresAt: null,
        previewVolumeUSD: 334.56,
      },
    ],
    referralAsReferee: undefined,
    previewVolumeUSD: 1234.56,
    isComputing: false,
    // Mirror the production contract: epochStart = today's UTC midnight,
    // nextEpochRunAt = tomorrow's UTC midnight. Anchoring to UTC midnight keeps
    // the mock-driven countdown in the header chip honest about time-to-drop
    // instead of always reading "24:00:00".
    ...(() => {
      const todayUtcMidnight = new Date();
      todayUtcMidnight.setUTCHours(0, 0, 0, 0);
      const tomorrowUtcMidnight = new Date(todayUtcMidnight.getTime() + 24 * 60 * 60 * 1000);
      return {
        epochStart: todayUtcMidnight.toISOString(),
        nextEpochRunAt: tomorrowUtcMidnight.toISOString(),
      };
    })(),
  },
  [API_ENDPOINT.HUB_REFEREE_ELIGIBILITY_PROGRESS]: {
    tradeVolumeUSD: {
      target: new Decimal(1),
      current: new Decimal(1),
    },
    exchangeAccountCount: {
      target: 1,
      current: 1,
    },
  },
  [API_ENDPOINT.HUB_REFERER_WEEKLY_ELIGIBILITY_PROGRESS]: {
    target: new Decimal(1),
    current: new Decimal(1),
  },
  [API_ENDPOINT.HUB_REFERRAL_REWARDS_CLAIMABLE]: {
    claimableEpochs: {
      claimablesData: [],
      allClaimsEncoded: "0x",
      batches: [],
    },
  },
  [API_ENDPOINT.HUB_REFERRAL_SETTINGS]: {
    refereeRewardSplitRatio: 0.5,
    referrerRewardSplitRatio: 0.5,
    totalBrokerFeeShareRatio: 0.5,
    exchangesRewardSplitRatio: {
      binance: { takerFeeRatio: 0.001, rebateToBrokerRatio: 0.0005 },
      kucoin: { takerFeeRatio: 0.001, rebateToBrokerRatio: 0.0005 },
      bybit: { takerFeeRatio: 0.001, rebateToBrokerRatio: 0.0005 },
      bitget: { takerFeeRatio: 0.001, rebateToBrokerRatio: 0.0005 },
      mexc: { takerFeeRatio: 0.001, rebateToBrokerRatio: 0.0005 },
      gateio: { takerFeeRatio: 0.001, rebateToBrokerRatio: 0.0005 },
      hyperliquid: { takerFeeRatio: 0.00005, rebateToBrokerRatio: 1 },
    } as APIResponseMap[API_ENDPOINT.HUB_REFERRAL_SETTINGS]["exchangesRewardSplitRatio"],
    refereeActivation: {
      minConnectedCexCount: 3,
      timeLimitDays: 7,
      minTradeVolumeUSD: 0,
    },
    ambassador: {
      bonusDurationDays: 30,
      totalBrokerFeeShareRatio: 0.3,
      totalBrokerFeeShareRatioBonus: 0.8,
    },
    ambassadorEligibility: {
      minTotalVolumeUSD: 50_000_000,
    },
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BUILDER_FEES]: {
    hyperliquid: "0.00005",
    tradexyz: "0.00005",
    flx: "0.00005",
    vntl: "0.00005",
    hyna: "0.00005",
    km: "0.00005",
    cash: "0.00005",
    grvt: "0.00005",
    extended: "0.00005",
    paradex: "0",
    zeroone: "0",
    hibachi: "0.0001",
    bitget: "0",
    mexc: "0",
    kucoin: "0",
    cryptocom: "0",
    bybit: "0",
  },
  [API_ENDPOINT.HUB_REFERRER_STATS]: {
    totalReferralsAsReferrer: 10,
    totalRewardsClaimed: 100,
    totalRewardsUnclaimed: 100,
    totalRewardsClaimableSoon: 100,
    totalVolumeMadeByAllReferees: 0,
  },
  [API_ENDPOINT.HUB_REFEREE_STATS]: {
    totalVolumeTraded: 100,
    totalRewardsClaimed: 100,
    totalRewardsUnclaimed: 100,
    totalRewardsClaimableSoon: 100,
  },
  [API_ENDPOINT.HUB_REFEREE_ELIGIBLE]: {
    eligible: true,
  },
  [API_ENDPOINT.HUB_REFERRAL_AMBASSADOR_ELIGIBILITY]: {
    isAmbassador: false,
    volume: {
      ownUSD: 26_400_000,
      refereesUSD: 0,
      totalUSD: 26_400_000,
      targetUSD: 50_000_000,
      progressRatio: 0.528,
    },
    isEligible: false,
    applyUrl: "https://discord.gg/ACu3BZVnT4",
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_CODE_COUNT]: 10,
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_REFEREES_COUNT]: {
    total: 10,
    lastSixMonths: { "2025-07-01": 100 },
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_VOLUME_BY_EXCHANGE]: [
    {
      exchangeId: "bybit",
      totalVolumeUSD: 10,
    },
  ],
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_VOLUME_BY_REFEREE]: {
    data: [
      {
        defiAddress: "0xfc7a35150ee0e",
        totalVolumeUSD: 10,
      },
    ],
    totalReferees: 100,
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_REWARDS_STATISTICS]: {
    totalDistributedRewards: 80000,
    totalRewards: 80000,
    distributedRewardsCount: 800,
    totalRewardsCount: 800,
    lastSixMonthsDistributedRewards: {
      "2025-02-01": 8000,
      "2025-03-01": 8000,
      "2025-04-01": 8000,
      "2025-05-01": 8000,
      "2025-06-01": 16000,
      "2025-07-01": 32000,
    },
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_VOLUME_BY_REFERRER]: {
    data: [
      {
        defiAddress: "0xfc7a35150ee0e",
        totalVolumeUSD: 65000,
        lastWeekVolumeUSD: 0,
        lastMonthVolumeUSD: 5000,
        totalRewardsUSD: 1000,
      },
    ],
    totalReferrers: 80,
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_REFERRER_REFEREES]: [
    {
      defiAddress: "0xfc7a35150ee0e",
      totalRewardsUSD: 100,
      splitRatio: 0.6,
    },
  ],
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_REFERRER_DETAILS]: {
    refereesCexAccountCount: 10,
    totalTradedVolume: 100,
    statusRepartition: { ACTIVE: 10, PENDING: 10, EXPIRED: 10, BANNED: 10, EXPIRED_AFTER_ACTIVATION: 10 },
    tradedVolumesByReferee: {
      "0xfc7a35150ee0e": 100,
      "0xfc7a35150ee0f": 100,
      "0xfc7a35150ee0g": 100,
      "0xfc7a35150ee0h": 100,
      "0xfc7a35150ee0i": 100,
    },
    referralRewardsByReferee: {
      "0xfc7a35150ee0e": new Decimal(100),
      "0xfc7a35150ee0f": new Decimal(100),
      "0xfc7a35150ee0g": new Decimal(100),
      "0xfc7a35150ee0h": new Decimal(100),
      "0xfc7a35150ee0i": new Decimal(100),
    },
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_ACTIVE_REFEREES]: {
    "2025-02-01": 79,
    "2025-03-01": 78,
    "2025-04-01": 79,
    "2025-05-01": 80,
    "2025-06-01": 78,
    "2025-07-01": 43,
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_REFERRAL_STATUS_RATE]: {
    totalReferrals: 10,
    countByStatus: { PENDING: 0, ACTIVE: 10, EXPIRED: 0, BANNED: 0, EXPIRED_AFTER_ACTIVATION: 0 },
  },
  [API_ENDPOINT.HUB_REFERRAL_MONITORING_QUEST_COMPLETION_TIME]: "3600",
  [API_ENDPOINT.HUB_REFERRAL_ADMIN_GENERATE_REFERRAL_CODE]: {
    referralCode: "CEDETEST",
    id: 1,
    hubUserId: 1,
    expiresAt: new Date(),
    referralSettingsOverride: null,
  },
  [API_ENDPOINT.QUEST_LIVE]: [],
  [API_ENDPOINT.QUEST_TMA_ONBOARDING_LIVE]: [],
  [API_ENDPOINT.QUEST_CLAIM_REWARDS]: {
    id: "completion-456",
    hubUserId: 32,
    questId: 4,
    questName: "Connect Binance",
    questType: "cex_connection",
    questFrequency: "once",
    rewardType: "lottery_ticket",
    rewardCount: new Decimal(1),
    rewardClaimed: true,
    rewardClaimedAt: new Date("2024-01-14T09:30:00Z"),
    completedAt: new Date("2024-01-12T10:15:00Z"),
    completionMetadata: {
      questType: "cex_connection",
      data: {
        exchange: "binance",
        exchangeUserId: "binance-user-123",
        connectedAt: new Date("2024-01-12T10:15:00Z"),
      },
    },
    periodStart: new Date("2024-01-01T00:00:00Z"),
    periodEnd: new Date("2024-12-31T23:59:59Z"),
    weekNumber: 2,
    dayOfYear: 12,
    monthNumber: 1,
    year: 2024,
    createdAt: new Date("2024-01-12T10:15:00Z"),
    updatedAt: new Date("2024-01-14T09:30:00Z"),
  },
  [API_ENDPOINT.QUEST_USER_COMPLETIONS]: [
    {
      id: "completion-456",
      hubUserId: 32,
      questId: 4,
      questName: "Connect Binance",
      questType: "cex_connection",
      questFrequency: "once",
      rewardType: "lottery_ticket",
      rewardCount: new Decimal(1),
      rewardClaimed: true,
      rewardClaimedAt: new Date("2024-01-14T09:30:00Z"),
      completedAt: new Date("2024-01-12T10:15:00Z"),
      completionMetadata: {
        questType: "cex_connection",
        data: {
          exchange: "binance",
          exchangeUserId: "binance-user-123",
          connectedAt: new Date("2024-01-12T10:15:00Z"),
        },
      },
      periodStart: new Date("2024-01-01T00:00:00Z"),
      periodEnd: new Date("2024-12-31T23:59:59Z"),
      weekNumber: 2,
      dayOfYear: 12,
      monthNumber: 1,
      year: 2024,
      createdAt: new Date("2024-01-12T10:15:00Z"),
      updatedAt: new Date("2024-01-14T09:30:00Z"),
    },
  ],
  [API_ENDPOINT.QUEST_USER_PROGRESS]: [
    {
      id: "progress-123",
      hubUserId: 32,
      questId: 6,
      questFrequency: "monthly",
      questType: "trading_volume",
      progress: new Decimal(45),
      metadata: {
        questType: "trading_volume",
        data: {
          currentVolume: new Decimal(4500),
          tradesCount: 28,
          lastTradeAt: new Date("2024-01-14T15:30:00Z"),
        },
      },
      isCompleted: false,
      lastProgressAt: new Date("2024-01-14T15:30:00Z"),
      periodStart: new Date("2024-01-01T00:00:00Z"),
      periodEnd: new Date("2024-01-31T23:59:59Z"),
      createdAt: new Date("2024-01-01T00:00:00Z"),
      updatedAt: new Date("2024-01-14T15:30:00Z"),
    },
    {
      id: "progress-456",
      hubUserId: 32,
      questId: 2,
      questFrequency: "weekly",
      questType: "trading_volume",
      progress: new Decimal(85),
      metadata: {
        questType: "trading_volume",
        data: {
          currentVolume: new Decimal(850),
          tradesCount: 15,
          lastTradeAt: new Date("2024-01-14T12:00:00Z"),
        },
      },
      isCompleted: false,
      lastProgressAt: new Date("2024-01-14T12:00:00Z"),
      periodStart: new Date("2024-01-08T00:00:00Z"),
      periodEnd: new Date("2024-01-14T23:59:59Z"),
      createdAt: new Date("2024-01-08T00:00:00Z"),
      updatedAt: new Date("2024-01-14T12:00:00Z"),
    },
  ],
  [API_ENDPOINT.QUEST_USER_ELIGIBILITY]: [
    {
      questId: 4,
      canComplete: false,
      reason: "Quest can only be completed once",
      lastCompletionDate: new Date("2024-01-12T10:15:00Z"),
    },
    {
      questId: 5,
      canComplete: false,
      reason: "Quest can only be completed once",
      lastCompletionDate: new Date("2024-01-13T16:20:00Z"),
    },
    {
      questId: 6,
      canComplete: true,
    },
    {
      questId: 2,
      canComplete: true,
      lastCompletionDate: new Date("2024-01-07T14:30:00Z"),
    },
  ],
  [API_ENDPOINT.QUEST_X_LIKE]: {
    success: true,
  },
  [API_ENDPOINT.QUEST_X_RETWEET]: {
    success: true,
  },
  [API_ENDPOINT.QUEST_X_FOLLOW]: {
    success: true,
  },
  [API_ENDPOINT.QUEST_POST_DATA]: {
    postData: {
      id: "123",
      text: "This is a test post",
      createdAt: new Date("2024-01-10T10:00:00Z"),
      author: {
        id: "123",
        username: "testuser",
        name: "Test User",
        avatarUrl: "https://x.com/testuser/profile_image.jpg",
      },
    },
    comment: "This is a test comment",
  },
  [API_ENDPOINT.QUEST_COMMENT_AND_FOLLOW]: { success: true },
  [API_ENDPOINT.QUEST_POST_AND_FOLLOW]: { success: true },
  [API_ENDPOINT.LOTTERY_USER_TICKETS]: {
    totalTickets: 5,
    tickets: [
      {
        id: "ticket-1",
        earnedAt: new Date("2024-01-10T10:00:00Z"),
        source: "quest_completion",
        questCompletionId: "completion-123",
      },
      {
        id: "ticket-2",
        earnedAt: new Date("2024-01-11T14:30:00Z"),
        source: "quest_completion",
        questCompletionId: "completion-456",
      },
    ],
    currentDraw: {
      id: "draw-period-1",
      name: "Week 2 - 2024",
      endDate: new Date("2024-01-14T23:59:59Z"),
      drawDate: new Date("2024-01-15T12:00:00Z"),
      totalTickets: 150,
      canAcceptTickets: true,
    },
  },
  [API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_DISCONNECTION]: [
    {
      id: 1,
      exchangeId: "binance",
      hubUserId: 1,
      accountId: "123",
      createdAt: new Date("2024-01-01T00:00:00Z"),
      reason: EXCHANGE_ACCOUNT_DISCONNECTION_REASONS.SUB_ACCOUNT_REMOVED_BY_ADMIN,
    },
  ],
  [API_ENDPOINT.HUB_USER_NICKNAME]: {
    nickname: "testuser",
  },
  [API_ENDPOINT.HUB_USER_NICKNAME_AVAILABLE]: {
    available: true,
  },
  [API_ENDPOINT.CEDE_ARENA_STARTER_PACK]: {
    items: [{ faceValueUsdt: "5", quantity: 2 }],
    ticketCount: 2,
    totalValueUsdt: "10",
  },
  [API_ENDPOINT.CEDE_ARENA_TICKET_LINK]: {
    slug: "mock-partner",
    partnerName: "Mock Partner",
    headline: null,
    status: "ACTIVE",
    items: [{ faceValueUsdt: "25", quantity: 3 }],
    ticketCount: 3,
    totalValueUsdt: "75",
    claimCount: 0,
    maxClaims: 1,
  },
  [API_ENDPOINT.CEDE_ARENA_TICKET_LINK_CLAIM]: {
    slug: "mock-partner",
    partnerName: "Mock Partner",
    ticketCount: 3,
    totalValueUsdt: "75",
  },
  [API_ENDPOINT.HUB_USER_PREFERRED_SOCIAL]: {
    socialName: "twitter",
  },
  [API_ENDPOINT.HUB_USER_SOCIAL_VISIBILITY]: {
    enabled: true,
  },
  [API_ENDPOINT.HUB_USER_PROFILE_AVATAR]: {
    avatarUrl: "https://cdn-dev.cedelabs.io/arena-avatars/mock.webp",
    avatarThumbUrl: "https://cdn-dev.cedelabs.io/arena-avatars/mock.thumb.webp",
  },
  [API_ENDPOINT.HUB_USER_PROFILE_AVATAR_DELETE]: { success: true },
  [API_ENDPOINT.HUB_USER_PROFILE_QUOTE]: { quote: null },
  [API_ENDPOINT.WEB_PUSH_SUBSCRIPTIONS]: {
    saved: true,
  },
  [API_ENDPOINT.HOLD_TO_EARN_LIVE_CAMPAIGNS]: [
    {
      id: "1",
      name: "Bitcoin Hold Campaign",
      title: "Hold BTC and Earn Rewards",
      shortDescription: "Hold Bitcoin in supported exchanges and earn daily rewards",
      tokenSymbol: "BTC",
      startAt: new Date("2024-01-15T00:00:00.000Z"),
      endAt: new Date("2024-02-15T23:59:59.000Z"),
      rewardToken: "USDT",
      rewardDistribution: "classic",
      // Backwards compatibility fields - derived from epochs for frontend
      minimumEligibleUsdBalance: 0.01,
      rewardRule: "proportional",
      rewardPoolAmount: 1000,
      exchangeIds: ["binance", "coinbase", "kucoin"],
      production: true,
      descriptions: [
        {
          title: "How it works",
          content: "Hold Bitcoin in your connected exchange accounts and earn daily USDT rewards",
        },
        {
          title: "Requirements",
          content: "Minimum 0.01 BTC balance across supported exchanges",
        },
      ],
      epochs: [
        {
          epochNumber: 1,
          activeEpochId: 0,
          startDate: new Date("2024-01-15T00:00:00.000Z"),
          endDate: new Date("2024-01-16T00:00:00.000Z"),
          production: true,
          isPaused: false,
          isFullyPaused: false,
          rewardPoolAmount: 1000,
          minimumEligibleUsdBalance: 0.01,
          exchangeIds: ["binance", "coinbase", "kucoin"],
          rewardRule: "proportional",
        },
        {
          epochNumber: 2,
          activeEpochId: 1,
          startDate: new Date("2024-01-16T00:00:00.000Z"),
          endDate: new Date("2024-01-17T00:00:00.000Z"),
          production: true,
          isPaused: false,
          isFullyPaused: false,
          rewardPoolAmount: 1000,
          minimumEligibleUsdBalance: 0.01,
          exchangeIds: ["binance", "coinbase", "kucoin"],
          rewardRule: "proportional",
        },
      ],
      networkReward: [REWARD_NETWORK.ETHEREUM, REWARD_NETWORK.POLYGON],
      visibility: "public",
      rewardVaultAddressProduction: "0x1234567890123456789012345678901234567890",
      uniqueParticipationByUser: true,
      requireTelegramVerification: false,
      requireDiscordVerification: false,
      requireTwitterVerification: false,
      icon: "/uploads/btc_icon.svg",
      tokenIcon: "/uploads/usdt_icon.svg",
      campaignPartners: ["Bitcoin Foundation"],
      createdAt: new Date("2024-01-10T10:00:00.000Z"),
      updatedAt: new Date("2024-01-10T10:00:00.000Z"),
    },
    {
      id: "2",
      name: "Ethereum Hold Campaign",
      title: "Hold ETH and Earn Rewards",
      shortDescription: "Hold Ethereum and earn weekly rewards in USDT",
      tokenSymbol: "ETH",
      startAt: new Date("2024-02-01T00:00:00.000Z"),
      endAt: new Date("2024-02-15T23:59:59.000Z"),
      rewardToken: "USDT",
      rewardDistribution: "classic",
      // Backwards compatibility fields - derived from epochs for frontend
      minimumEligibleUsdBalance: 0.1,
      rewardRule: "fixed_spot",
      rewardPoolAmount: 2000,
      numberOfSpots: 100,
      exchangeIds: ["binance", "bybit", "gateio"],
      production: true,
      descriptions: [
        {
          title: "Premium Campaign",
          content: "Exclusive Ethereum holding campaign with enhanced rewards",
        },
      ],
      epochs: [
        {
          epochNumber: 1,
          activeEpochId: 0,
          startDate: new Date("2024-02-01T00:00:00.000Z"),
          endDate: new Date("2024-02-08T00:00:00.000Z"),
          production: true,
          isPaused: false,
          isFullyPaused: false,
          rewardPoolAmount: 2000,
          numberOfSpots: 100,
          minimumEligibleUsdBalance: 0.1,
          exchangeIds: ["binance", "bybit", "gateio"],
          rewardRule: "fixed_spot",
        },
      ],
      networkReward: [REWARD_NETWORK.ETHEREUM, REWARD_NETWORK.ARBITRUM_ONE],
      visibility: "private",
      privateCampaignPartner: "ethereum-foundation",
      privateCampaignUtm: "eth-hold-2024",
      rewardVaultAddressProduction: "0x2345678901234567890123456789012345678901",
      rewardVaultAddressDevelopment: "0x3456789012345678901234567890123456789012",
      rewardVaultAddressStaging: "0x4567890123456789012345678901234567890123",
      uniqueParticipationByUser: false,
      requireTelegramVerification: true,
      requireDiscordVerification: true,
      requireTwitterVerification: false,
      icon: "/uploads/eth_icon.svg",
      tokenIcon: "/uploads/usdt_icon.svg",
      campaignPartners: ["Ethereum Foundation"],
      createdAt: new Date("2024-01-20T10:00:00.000Z"),
      updatedAt: new Date("2024-01-20T10:00:00.000Z"),
    },
  ],
  [API_ENDPOINT.HOLD_TO_EARN_ALL_CAMPAIGNS]: [
    {
      id: "1",
      name: "Bitcoin Hold Campaign",
      title: "Hold BTC and Earn Rewards",
      shortDescription: "Hold Bitcoin in supported exchanges and earn daily rewards",
      tokenSymbol: "BTC",
      startAt: new Date("2024-01-15T00:00:00.000Z"),
      endAt: new Date("2024-02-15T23:59:59.000Z"),
      rewardToken: "USDT",
      rewardDistribution: "classic",
      minimumEligibleUsdBalance: 0.01,
      rewardRule: "proportional",
      rewardPoolAmount: 1000,
      exchangeIds: ["binance", "coinbase", "kucoin"],
      production: true,
      descriptions: [
        {
          title: "How it works",
          content: "Hold Bitcoin in your connected exchange accounts and earn daily USDT rewards",
        },
      ],
      epochs: [
        {
          epochNumber: 1,
          activeEpochId: 0,
          startDate: new Date("2024-01-15T00:00:00.000Z"),
          endDate: new Date("2024-01-16T00:00:00.000Z"),
          production: true,
          isPaused: false,
          isFullyPaused: false,
          rewardPoolAmount: 1000,
          numberOfSpots: 100,
          minimumEligibleUsdBalance: 0.01,
          exchangeIds: ["binance", "coinbase"],
          rewardRule: "proportional",
        },
      ],
      networkReward: [REWARD_NETWORK.POLYGON],
      visibility: "public",
      rewardVaultAddressProduction: "0x1234567890abcdef",
      uniqueParticipationByUser: true,
      requireTelegramVerification: false,
      requireDiscordVerification: false,
      requireTwitterVerification: false,
      createdAt: new Date("2024-01-10T10:00:00.000Z"),
      updatedAt: new Date("2024-01-10T10:00:00.000Z"),
    },
    {
      id: "2",
      name: "Ethereum Hold Campaign",
      title: "Hold ETH and Earn Rewards",
      shortDescription: "Hold Ethereum and get rewarded",
      tokenSymbol: "ETH",
      startAt: new Date("2024-02-01T00:00:00.000Z"),
      endAt: new Date("2024-03-01T23:59:59.000Z"),
      rewardToken: "USDC",
      rewardDistribution: "classic",
      minimumEligibleUsdBalance: 0.1,
      rewardRule: "fixed_spot",
      rewardPoolAmount: 2000,
      exchangeIds: ["binance", "kraken"],
      production: false,
      descriptions: [
        {
          title: "How it works",
          content: "Hold Ethereum in supported exchanges and earn USDC rewards",
        },
      ],
      epochs: [
        {
          epochNumber: 1,
          activeEpochId: 0,
          startDate: new Date("2024-02-01T00:00:00.000Z"),
          endDate: new Date("2024-02-02T00:00:00.000Z"),
          production: false,
          isPaused: false,
          isFullyPaused: false,
          rewardPoolAmount: 2000,
          numberOfSpots: 50,
          minimumEligibleUsdBalance: 0.1,
          exchangeIds: ["binance", "kraken"],
          rewardRule: "fixed_spot",
        },
      ],
      networkReward: [REWARD_NETWORK.ETHEREUM],
      visibility: "private",
      privateCampaignPartner: "Ethereum Foundation",
      rewardVaultAddressProduction: "0xabcdef1234567890",
      uniqueParticipationByUser: true,
      requireTelegramVerification: true,
      requireDiscordVerification: false,
      requireTwitterVerification: true,
      createdAt: new Date("2024-01-20T10:00:00.000Z"),
      updatedAt: new Date("2024-01-20T10:00:00.000Z"),
    },
  ],
  [API_ENDPOINT.HOLD_TO_EARN_CAMPAIGN_BY_NAME]: {
    id: "1",
    name: "Bitcoin Hold Campaign",
    title: "Hold BTC and Earn Rewards",
    shortDescription: "Hold Bitcoin in supported exchanges and earn daily rewards",
    tokenSymbol: "BTC",
    startAt: new Date("2024-01-15T00:00:00.000Z"),
    endAt: new Date("2024-02-15T23:59:59.000Z"),
    rewardToken: "USDT",
    rewardDistribution: "classic",
    minimumEligibleUsdBalance: 0.01,
    rewardRule: "proportional",
    rewardPoolAmount: 1000,
    exchangeIds: ["binance", "coinbase", "kucoin"],
    production: true,
    descriptions: [
      {
        title: "How it works",
        content: "Hold Bitcoin in your connected exchange accounts and earn daily USDT rewards",
      },
      {
        title: "Requirements",
        content: "Minimum 0.01 BTC balance across supported exchanges",
      },
    ],
    epochs: [
      {
        epochNumber: 1,
        activeEpochId: 0,
        startDate: new Date("2024-01-15T00:00:00.000Z"),
        endDate: new Date("2024-01-16T00:00:00.000Z"),
        production: true,
        isPaused: false,
        isFullyPaused: false,
        rewardPoolAmount: 1000,
        numberOfSpots: 100,
        minimumEligibleUsdBalance: 0.01,
        exchangeIds: ["binance", "coinbase", "kucoin"],
        rewardRule: "proportional",
      },
      {
        epochNumber: 2,
        activeEpochId: 1,
        startDate: new Date("2024-01-16T00:00:00.000Z"),
        endDate: new Date("2024-01-17T00:00:00.000Z"),
        production: true,
        isPaused: false,
        isFullyPaused: false,
        rewardPoolAmount: 1000,
        numberOfSpots: 100,
        minimumEligibleUsdBalance: 0.01,
        exchangeIds: ["binance", "coinbase", "kucoin"],
        rewardRule: "proportional",
      },
    ],
    networkReward: [REWARD_NETWORK.POLYGON],
    visibility: "public",
    rewardVaultAddressProduction: "0x1234567890abcdef",
    rewardVaultAddressDevelopment: "0xdev1234567890abcdef",
    rewardVaultAddressStaging: "0xstaging1234567890abcdef",
    uniqueParticipationByUser: true,
    requireTelegramVerification: false,
    requireDiscordVerification: false,
    requireTwitterVerification: false,
    icon: "bitcoin-icon.png",
    tokenIcon: "btc-token.png",
    campaignPartners: ["Bitcoin Foundation"],
    createdAt: new Date("2024-01-10T10:00:00.000Z"),
    updatedAt: new Date("2024-01-10T10:00:00.000Z"),
  },
  [API_ENDPOINT.HOLD_TO_EARN_EPOCH_STATISTICS]: {
    campaignId: "1",
    epochId: 1,
    statistics: {
      totalParticipants: 150,
      activeParticipants: 120,
      holdingParticipants: 85,
      optedOutParticipants: 30,
      totalVolume: "1250000.50",
    },
    leaderboardLastComputedAt: new Date("2025-01-15T10:00:00Z"),
    leaderboard: [
      {
        id: "leaderboard-record-1",
        hubUserId: 1001,
        defiAddress: "0x1234567890abcdef1234567890abcdef12345678",
        rank: 1,
        tokenAmountAverage: "5.75",
        tokenAmount: "5.75",
        tokenSymbol: "BTC",
        rewardTokenAmount: "287.50",
        rewardTokenSymbol: "USDT",
        rewardTokenAddress: "0xusdt1234567890abcdef",
        hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
        rewardClaimed: false,
        rewardClaimedAt: null,
        createdAt: new Date("2024-01-01T00:00:00Z"),
        updatedAt: new Date("2024-01-01T00:00:00Z"),
        participantStatus: "holding",
        isTopHolder: false,
        isAmbassador: false,
      },
      {
        id: "leaderboard-record-2",
        hubUserId: 1002,
        defiAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
        rank: 2,
        tokenAmountAverage: "3.25",
        tokenAmount: "3.25",
        tokenSymbol: "BTC",
        rewardTokenAmount: "162.50",
        rewardTokenSymbol: "USDT",
        rewardTokenAddress: "0xusdt1234567890abcdef",
        hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
        rewardClaimed: false,
        rewardClaimedAt: null,
        createdAt: new Date("2024-01-01T00:00:00Z"),
        updatedAt: new Date("2024-01-01T00:00:00Z"),
        participantStatus: "holding",
        isTopHolder: false,
        isAmbassador: false,
      },
      {
        id: "leaderboard-record-3",
        hubUserId: 1003,
        defiAddress: "0x567890abcdef1234567890abcdef1234567890ab",
        rank: 3,
        tokenAmountAverage: "0.00",
        tokenAmount: "0.00",
        tokenSymbol: "BTC",
        rewardTokenAmount: "0.00",
        rewardTokenSymbol: "USDT",
        rewardTokenAddress: "0xusdt1234567890abcdef",
        hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
        rewardClaimed: false,
        rewardClaimedAt: null,
        createdAt: new Date("2024-01-01T00:00:00Z"),
        updatedAt: new Date("2024-01-01T00:00:00Z"),
        participantStatus: "participating",
        isTopHolder: false,
        isAmbassador: false,
      },
      {
        id: "leaderboard-record-4",
        hubUserId: 1004,
        rank: 4,
        tokenAmountAverage: "1.50",
        tokenAmount: "1.50",
        tokenSymbol: "BTC",
        rewardTokenAmount: "75.00",
        rewardTokenSymbol: "USDT",
        rewardTokenAddress: "0xusdt1234567890abcdef",
        hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
        rewardClaimed: false,
        rewardClaimedAt: null,
        createdAt: new Date("2024-01-01T00:00:00Z"),
        updatedAt: new Date("2024-01-01T00:00:00Z"),
        participantStatus: "opted-out",
        isTopHolder: false,
        isAmbassador: false,
      },
      {
        id: "leaderboard-record-5",
        hubUserId: 1005,
        rank: 5,
        tokenAmountAverage: "0.00",
        tokenAmount: "0.00",
        tokenSymbol: "BTC",
        rewardTokenAmount: "0.00",
        rewardTokenSymbol: "USDT",
        rewardTokenAddress: "0xusdt1234567890abcdef",
        hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
        rewardClaimed: false,
        rewardClaimedAt: null,
        createdAt: new Date("2024-01-01T00:00:00Z"),
        updatedAt: new Date("2024-01-01T00:00:00Z"),
        participantStatus: "not-participating",
        isTopHolder: false,
        isAmbassador: false,
      },
    ],
  },
  [API_ENDPOINT.HOLD_TO_EARN_VALIDATE_DEFI_ADDRESS]: {
    isValid: true,
  },
  [API_ENDPOINT.TRADE_TO_EARN_CAMPAIGNS]: { campaigns: [], cursor: "", totalCount: 0, stats: {} },
  [API_ENDPOINT.TRADE_TO_EARN_CAMPAIGNS_AUTHENTICATED]: { campaigns: [], cursor: "", totalCount: 0, stats: {} },
  [API_ENDPOINT.TRADE_TO_EARN_CAMPAIGN_BY_NAME]: {} as TradeToEarnCampaignWithStats,
  [API_ENDPOINT.TRADE_TO_EARN_CAMPAIGN_BY_NAME_AUTHENTICATED]: {} as TradeToEarnCampaignWithStats,
  [API_ENDPOINT.TRADE_TO_EARN_CAMPAIGN_BY_NAME_INTERNAL]: {} as TradeToEarnCampaignWithStats,
  [API_ENDPOINT.ADMIN_HTE_CAMPAIGNS]: { campaigns: [], totalCount: 0, page: 1 },
  [API_ENDPOINT.ADMIN_HTE_CAMPAIGN_BY_NAME]: {} as HteCampaignFullDataDto,
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGNS]: [] as TradeToEarnCampaignSummary[],
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_ANALYTICS_LIST]: [] as CampaignAnalyticsListItem[],
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_ANALYTICS_EPOCHS]: {
    name: "",
    rewardToken: null,
    epochs: [],
    total: 0,
  } as CampaignAnalyticsEpochs,
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_VAULT_DETAIL]: {} as CampaignVaultDetail,
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_ANALYTICS_EPOCH_GROUPS]: {
    name: "",
    rewardToken: null,
    groups: [],
    totalEpochs: 0,
  } as CampaignAnalyticsEpochGroups,
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_VAULT_LIST]: [] as CampaignVaultListItem[],
  [API_ENDPOINT.HOLD_TO_EARN_JOIN_CAMPAIGN]: {
    success: true,
    message: "Successfully registered for campaign",
  },
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_BY_NAME]: {} as TradeToEarnCampaign,
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_PREVIEWS]: { campaigns: [], totalCount: 0, page: 1 },
  [API_ENDPOINT.TRADE_TO_EARN_CAMPAIGN_BY_REWARD_VAULT_ADDRESS]: {} as TradeToEarnCampaignSummary,
  [API_ENDPOINT.CEDE_ARENA_LEADERBOARD]: { entries: [], totalCount: 0, page: 0, size: 25 },
  [API_ENDPOINT.CEDE_ARENA_LEADERBOARD_POSITION]: { entry: null },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS]: { friendHubUserIds: [], pendingRequests: [], outgoingRequestHubUserIds: [] },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_LIST]: { friends: [], nextCursor: null },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_REQUEST]: {
    friendHubUserIds: [],
    pendingRequests: [],
    outgoingRequestHubUserIds: [],
  },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_REQUEST_RESPOND]: {
    friendHubUserIds: [],
    pendingRequests: [],
    outgoingRequestHubUserIds: [],
  },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_REMOVE]: {
    friendHubUserIds: [],
    pendingRequests: [],
    outgoingRequestHubUserIds: [],
  },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_LOOKUP]: { user: null },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_INVITE_CREATE]: { slug: "mock-invite" },
  [API_ENDPOINT.CEDE_ARENA_FRIENDS_INVITE_CLAIM]: {
    inviter: { hubUserId: 1, nickname: null, socialUsername: null },
  },
  [API_ENDPOINT.CEDE_ARENA_LEADERBOARD_STATS]: { totalRewardsPaidUsdt: "0", totalTraders: 0 },
  [API_ENDPOINT.CEDE_ARENA_WEEKLY_LEADERBOARD]: {
    ...MOCK_ARENA_WEEK,
    entries: [],
    totalCount: 0,
    page: 0,
    size: 25,
  },
  [API_ENDPOINT.CEDE_ARENA_WEEKLY_LEADERBOARD_POSITION]: { ...MOCK_ARENA_WEEK, entry: null },
  [API_ENDPOINT.CEDE_ARENA_WEEKLY_LEADERBOARD_POSITIONS]: { ...MOCK_ARENA_WEEK, entries: [] },
  [API_ENDPOINT.CEDE_ARENA_WEEKLY_LEADERBOARD_CROWNS]: { ...MOCK_ARENA_WEEK, crowns: [] },
  [API_ENDPOINT.CEDE_ARENA_PLAYER_PROFILE]: {
    profile: {
      hubUserId: 1,
      nickname: null,
      avatarUrl: null,
      avatarThumbUrl: null,
      quote: null,
      cumulativeWinningsUsdt: "0",
      wins: 0,
      bestWinStreak: 0,
      rankedPlayed: 0,
      socials: [],
    },
  },
  [API_ENDPOINT.CEDE_ARENA_PLAYER_IDENTITIES]: { identities: [] },
  [API_ENDPOINT.CEDE_ARENA_ME_ENTRY]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_OPT_IN]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_READY]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_DECLINE]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_LEAVE]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_ONBOARDING_SKIP]: { status: "SKIPPED" },
  [API_ENDPOINT.CEDE_ARENA_ONBOARDING_COMPLETE]: { status: "COMPLETED" },
  [API_ENDPOINT.CEDE_ARENA_TICKETS]: {
    tickets: [
      { id: "mock-ticket-1", faceValueUsdt: "0.5", expiresAt: "2099-01-01T00:00:00.000Z", source: "STARTER_PACK" },
      { id: "mock-ticket-2", faceValueUsdt: "1", expiresAt: "2099-01-02T00:00:00.000Z", source: "STARTER_PACK" },
    ],
  },
  [API_ENDPOINT.CEDE_ARENA_QUEST_STATE]: {
    status: "INACTIVE",
    expiresAt: null,
    depositDone: false,
    depositedUsdt: "0",
    competitionDone: false,
    requiredBuyInUsdt: "10",
    reward: [],
  },
  [API_ENDPOINT.CEDE_ARENA_QUEST_PREVIEW]: {
    enabled: true,
    requiredDepositUsdt: "10",
    requiredBuyInUsdt: "10",
    windowHours: 24,
    reward: [{ faceValueUsdt: "5", quantity: 2 }],
    totalValueUsdt: "10",
  },
  [API_ENDPOINT.CEDE_ARENA_ONBOARDING_STATE]: { step: "inactive", duelCampaignSlug: null },
  [API_ENDPOINT.CEDE_ARENA_CAMPAIGN_OPT_IN]: { campaignName: "mock-arena-campaign", status: "IN" },
  [API_ENDPOINT.CEDE_ARENA_CAMPAIGN_OPT_OUT]: { campaignName: "mock-arena-campaign", status: "OUT" },
  [API_ENDPOINT.CEDE_ARENA_MY_CAMPAIGN_OPT_INS]: { campaignNames: [] },
  [API_ENDPOINT.CEDE_ARENA_CAMPAIGN_OPT_IN_COUNTS]: { counts: [] },
  [API_ENDPOINT.CEDE_ARENA_CAMPAIGN_JOINERS]: { campaignName: "mock-campaign", total: 0, joiners: [] },
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_CREATE_ROOM]: MOCK_PRIVATE_COMPETITION_ROOM,
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_MY_ROOMS]: { rooms: [MOCK_PRIVATE_COMPETITION_ROOM], nextCursor: null },
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_GET_ROOM]: MOCK_PRIVATE_COMPETITION_ROOM,
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_JOIN_ROOM]: MOCK_PRIVATE_COMPETITION_ROOM,
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_LEAVE_ROOM]: MOCK_PRIVATE_COMPETITION_ROOM,
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_KICK_PARTICIPANT]: MOCK_PRIVATE_COMPETITION_ROOM,
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_UPDATE_CONFIG]: MOCK_PRIVATE_COMPETITION_ROOM,
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_START]: {
    ...MOCK_PRIVATE_COMPETITION_ROOM,
    status: "STARTED" as const,
    campaignName: "mock-private-arena",
  },
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_CANCEL_ROOM]: { ...MOCK_PRIVATE_COMPETITION_ROOM, status: "CANCELLED" as const },
  [API_ENDPOINT.CEDE_ARENA_PRIVATE_RAKE_POLICY]: { platformRakeBps: 0 },
  [API_ENDPOINT.CEDE_ARENA_DISCORD_STATUS]: {
    linked: false,
    inGuild: false,
    inviteUrl: "https://discord.gg/GQWmXsk7KR",
    discordUserId: null,
    discordUsername: null,
  },
  [API_ENDPOINT.CEDE_ARENA_QUALIFICATION]: {
    hasAnswered: false,
    questions: MOCK_ARENA_QUALIFICATION_QUESTIONS,
  },
  [API_ENDPOINT.CEDE_ARENA_QUALIFICATION_SKIP]: {
    hasAnswered: true,
    questions: MOCK_ARENA_QUALIFICATION_QUESTIONS,
  },
  [API_ENDPOINT.CEDE_ARENA_SURVEY]: {
    hasAnswered: false,
    hasParticipated: true,
    questions: [
      { id: "enjoyment", text: "How much did you enjoy the competition?" },
      { id: "trade_ease", text: "How easy was it to trade?" },
      { id: "compete_again", text: "How likely are you to compete again?" },
    ],
  },
  [API_ENDPOINT.CEDE_ARENA_SURVEY_SKIP]: {
    hasAnswered: true,
    hasParticipated: true,
    questions: [
      { id: "enjoyment", text: "How much did you enjoy the competition?" },
      { id: "trade_ease", text: "How easy was it to trade?" },
      { id: "compete_again", text: "How likely are you to compete again?" },
    ],
  },
  [API_ENDPOINT.CEDE_ARENA_MARKET_FAVORITES]: { pairSymbols: ["BTC/USDC:USDC"] },
  [API_ENDPOINT.CEDE_ARENA_QUEUE_STATUS]: { readyToTrade: 0, waitingForPayment: 0 },
  [API_ENDPOINT.CEDE_ARENA_TRAFFIC_CURVE]: { status: "DISABLED" },
  [API_ENDPOINT.CEDE_ARENA_GEO_ELIGIBILITY]: { blocked: false, countryCode: "FR" },
  [API_ENDPOINT.CEDE_ARENA_REMATCH_PROPOSE]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_REMATCH_ELIGIBILITY]: { canPropose: false, entryFeesUsdt: null, proposeWindowEndsAt: null },
  [API_ENDPOINT.CEDE_ARENA_FORFEIT]: {
    entry: null,
    entries: [],
    entryFeeWaived: false,
    matchmaking: {
      estimatedWaitMs: 60_000,
      readyCheckTimeoutMs: 30_000,
      autoAcceptReadyCheckTimeoutMs: 3_000,
      rematchCooldownMs: 10_000,
    },
  },
  [API_ENDPOINT.CEDE_ARENA_FORFEIT_ELIGIBILITY]: { canForfeit: false, reason: "DUEL_NOT_LIVE" },
  [API_ENDPOINT.CEDE_ARENA_FINALIZATION]: { campaignName: "mock-arena-campaign", epochId: 0, finalizedAt: new Date() },
  [API_ENDPOINT.CEDE_ARENA_CAMPAIGN_ACCOUNT_PNLS]: { pnls: [] },
  [API_ENDPOINT.CEDE_ARENA_BATTLE_ROYALE_STATE]: {
    campaignName: "mock-arena-campaign",
    eliminationIntervalSeconds: 120,
    firstEliminationSeconds: null,
    finalEliminationSeconds: null,
    elimination: { mode: "ONE" },
    finalDuel: true,
    startsAt: new Date(0),
    endsAt: new Date(240_000),
    playerCount: 3,
    roster: [],
    eliminations: [],
  },
  [API_ENDPOINT.CEDE_ARENA_OPENING_MOVE]: { openingMove: null, canArmOpeningMove: false },
  [API_ENDPOINT.CEDE_ARENA_CANCELLED_COMPETITION]: {
    campaignName: "mock-arena-campaign",
    title: "Mock Arena",
    startsAt: new Date(0),
    cancelledAt: new Date(0),
    reason: "NOT_ENOUGH_PLAYERS",
    minPlayers: 3,
    joinedCount: 1,
    entryFeesUsdt: "5",
    isBattleRoyale: true,
  },
  [API_ENDPOINT.CEDE_ARENA_BATTLE_RECORD]: {
    campaignName: "mock-arena-campaign",
    epochId: 0,
    format: "spin-and-go",
    startedAt: new Date(0),
    endedAt: new Date(300_000),
    startingCollateralUsd: "10000",
    seriesSourceResolution: "1s",
    players: [],
    cuts: [],
  },
  [API_ENDPOINT.CEDE_ARENA_ME_CAMPAIGN_STAKE]: null,
  [API_ENDPOINT.CEDE_ARENA_PAYOUT_CAPS_INTERNAL]: { caps: [] },
  [API_ENDPOINT.ADMIN_CEDE_ARENA_ENTRIES]: { entries: [], totalCount: 0, page: 0, size: 50 },
  [API_ENDPOINT.ADMIN_ARENA_OPS_OVERVIEW_KPIS]: {} as ArenaOpsOverviewKpisResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_OVERVIEW_COVERAGE_RINGS]: {} as ArenaOpsOverviewCoverageRingsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_OVERVIEW_RECONCILIATION]: {} as ArenaOpsOverviewReconciliationResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_USERS]: {} as ArenaOpsUsersResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_USERS_KPIS]: {} as ArenaOpsUsersKpisResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_USERS_LEDGER_IDENTITY]: {} as ArenaOpsUsersLedgerIdentityResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_USERS_BALANCE_DISTRIBUTION]: {} as ArenaOpsUsersBalanceDistributionResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_USERS_MOVEMENTS]: {} as ArenaOpsUsersMovementsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_PERIOD_FLOWS]: {} as ArenaOpsMoneyPeriodFlowsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_HOUSE_INJECTION]: {} as ArenaOpsMoneyHouseInjectionResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_RAKE_PROVENANCE]: {} as ArenaOpsMoneyRakeProvenanceResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_REVENUE_PER_DAY]: {} as ArenaOpsMoneyRevenuePerDayResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_DEPOSITS]: {} as ArenaOpsMoneyDepositsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_WITHDRAWALS]: {} as ArenaOpsMoneyWithdrawalsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_FREE_TICKET_VALUE]: {} as ArenaOpsMoneyFreeTicketValueResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_SANKEY]: {} as ArenaOpsMoneySankeyResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_MOVEMENTS]: {} as ArenaOpsMoneyMovementsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MONEY_WITHDRAWAL_REQUESTS]: {} as ArenaOpsMoneyWithdrawalRequestsResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_TICKETS_LIFECYCLE]: {} as ArenaOpsTicketsLifecycleResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_TICKETS_BY_SOURCE]: {} as ArenaOpsTicketsBySourceResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_TICKET_LINKS]: {} as ArenaOpsTicketLinksResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_TICKETS_OUTCOMES]: {} as ArenaOpsTicketsOutcomesResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_UNIVERSE]: {} as ArenaOpsMatchmakingUniverseResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_PAIRING_RATE_SERIES]: {} as ArenaOpsMatchmakingPairingRateSeriesResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_WAIT_SERIES]: {} as ArenaOpsMatchmakingWaitSeriesResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_WAIT_CDF]: {} as ArenaOpsMatchmakingWaitCdfResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_ENTRIES_BY_BUY_IN]: {} as ArenaOpsMatchmakingEntriesByBuyInResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_QUEUE_HEALTH]: {} as ArenaOpsMatchmakingQueueHealthResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_MATCHES_BY_HOUR]: {} as ArenaOpsMatchmakingMatchesByHourResponse,
  [API_ENDPOINT.ADMIN_ARENA_OPS_MATCHMAKING_QUEUE_DEPTH]: {} as ArenaOpsMatchmakingQueueDepthResponse,
  [API_ENDPOINT.ADMIN_CEDE_ARENA_MARK_PAID]: {} as AdminMarkArenaEntryPaidResponse,
  [API_ENDPOINT.ADMIN_CEDE_ARENA_CANCEL]: {} as AdminCancelArenaEntryResponse,
  [API_ENDPOINT.CEDE_ARENA_BALANCE]: {
    freeUsdt: "0",
    lockedUsdt: "0",
    totalUsdt: "0",
    pendingUsdt: "0",
    totalWonUsdt: "0",
  },
  [API_ENDPOINT.CEDE_ARENA_DEPOSIT_ADDRESS]: { depositAddress: "0x0000000000000000000000000000000000000000" },
  [API_ENDPOINT.CEDE_ARENA_DEPOSIT]: {
    depositAddress: "0x0000000000000000000000000000000000000000",
  },
  [API_ENDPOINT.CEDE_ARENA_DEPOSIT_METHOD]: { recorded: true },
  [API_ENDPOINT.CEDE_ARENA_DEPOSIT_METHODS]: { fiat: "crossmint", crypto: "unifold", countryCode: null },
  [API_ENDPOINT.CEDE_ARENA_CROSSMINT_SETUP]: { signerId: "", policyIds: [], wallet: null, receiptEmail: null },
  [API_ENDPOINT.CEDE_ARENA_CROSSMINT_WALLET]: {
    address: "0x0000000000000000000000000000000000000000",
    ownershipVerified: true,
    verificationChallenge: null,
  },
  [API_ENDPOINT.CEDE_ARENA_CROSSMINT_WALLET_VERIFY]: { ownershipVerified: true },
  [API_ENDPOINT.CEDE_ARENA_CROSSMINT_ORDERS]: { orderId: "", clientSecret: "" },
  [API_ENDPOINT.CEDE_ARENA_CROSSMINT_ORDER]: {
    orderId: "",
    status: "CREATED" as const,
    creditedAmount: null,
  },
  [API_ENDPOINT.CEDE_ARENA_CROSSMINT_OPEN_ORDERS]: { orders: [] },
  [API_ENDPOINT.CEDE_ARENA_WITHDRAW]: {
    withdraw: {
      id: 1,
      epochId: 0,
      amountUsdt: "0",
      status: "WAITING",
      vaultAddress: "0x0000000000000000000000000000000000000000",
      cooldownEnd: null,
      claimTxHash: null,
      createdAt: new Date(0).toISOString(),
    },
  },
  [API_ENDPOINT.CEDE_ARENA_WITHDRAWS]: { withdraws: [] },
  [API_ENDPOINT.CEDE_ARENA_WITHDRAW_CLAIM]: {
    claims: [
      {
        rewardsModule: "0x0000000000000000000000000000000000000000",
        cedeSafe: "0x0000000000000000000000000000000000000000",
        epoch: 0,
        user: "0x0000000000000000000000000000000000000000",
        amount: "0",
        proof: [],
      },
    ],
  },
  [API_ENDPOINT.CEDE_ARENA_MOVEMENTS]: { items: [], total: 0, page: 1, limit: 20, totalPages: 0 },
  [API_ENDPOINT.CEDE_ARENA_RECENT_PAYOUTS]: { payouts: [] },
  [API_ENDPOINT.CEDE_ARENA_FEED]: { items: [] },
  [API_ENDPOINT.CEDE_ARENA_CHAT_MESSAGES]: { messages: [] },
  [API_ENDPOINT.CEDE_ARENA_CHAT_SEND]: { accepted: true },
  [API_ENDPOINT.ARENA_SUPPORT_CONVERSATION]: { active: null, recent: [] },
  [API_ENDPOINT.ARENA_SUPPORT_MESSAGES]: { messages: [], hasMore: false },
  [API_ENDPOINT.ARENA_SUPPORT_SEND]: { accepted: true },
  [API_ENDPOINT.ARENA_SUPPORT_SEEN]: { accepted: true },
  [API_ENDPOINT.ARENA_SUPPORT_RESOLVE]: { accepted: true },
  [API_ENDPOINT.ARENA_SUPPORT_RATE]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_CONVERSATIONS]: { conversations: [], hasMore: false },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_MESSAGES]: { messages: [], hasMore: false },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_REPLY]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_RESOLVE]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_TAKE_OVER]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_HAND_BACK]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_REVIEW_MESSAGE]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_KB]: { entries: [] },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_KB_RETRY_EMBEDDING]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_PLAYGROUND]: {
    trace: {
      outcome: "FAILURE",
      failureReason: "NO_READY_ENTRIES",
      failureDetail: null,
      lowConfidenceReason: null,
      requestIndex: null,
      requestCount: null,
      requestSubject: null,
      rewrittenQuery: null,
      language: null,
      intent: null,
      candidates: [],
      draft: null,
      citations: [],
      verification: null,
      latency: {
        rewriteMs: null,
        retrievalMs: null,
        rerankMs: null,
        generateMs: null,
        verifyMs: null,
        translateMs: null,
        totalMs: 0,
      },
      inputTokens: 0,
      outputTokens: 0,
    },
  },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_RUNS]: { runs: [], hasMore: false },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_GOLDEN]: { cases: [] },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_GOLDEN_CREATE]: {
    id: "golden-1",
    topic: "DEPOSITS",
    query: "How do I deposit?",
    expectedEntryIds: ["kb-1"],
    expectedPath: "ANSWER",
    source: "MANUAL",
    createdBy: null,
    createdAt: 0,
  },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_GOLDEN_DELETE]: { accepted: true },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_EVAL_RUN]: { evalRunId: "eval-1" },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_EVAL_RUNS]: { runs: [] },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_EVAL_RUN_BY_ID]: {
    id: "eval-1",
    status: "RUNNING",
    metrics: null,
    results: [],
    error: null,
    models: {},
    startedBy: null,
    createdAt: 0,
    finishedAt: null,
  },
  [API_ENDPOINT.CMS_ARENA_SUPPORT_AGENT_DASHBOARD]: {
    ttfr: { p50Ms: null, p95Ms: null, count: 0 },
    ttfur: { p50Ms: null, p95Ms: null, count: 0 },
    resolvedConversations: 0,
    resolvedWithoutOpsRate: null,
    runs: 0,
    liveRequests: 0,
    outcomes: {
      CONFIDENT: 0,
      LOW_CONFIDENCE_REPHRASE: 0,
      LOW_CONFIDENCE_HANDOFF: 0,
      FAILURE: 0,
      SKIPPED: 0,
      GREETING: 0,
      THANKS: 0,
      FEEDBACK: 0,
      BUG_REPORT_HANDOFF: 0,
      WAITING_FOR_TEAM: 0,
      DETAILS_REQUESTED: 0,
    },
    failureReasons: {},
    handoffRate: null,
    rephraseRate: null,
    aiMessages: 0,
    reviewedAiMessages: 0,
    wrongAiMessages: 0,
    wrongAnswerRate: null,
    csat: {
      aiOnly: { average: null, count: 0 },
      humanOnly: { average: null, count: 0 },
      mixed: { average: null, count: 0 },
      noAnswer: { average: null, count: 0 },
    },
    latency: { p50Ms: null, p95Ms: null, count: 0 },
    daily: [],
  },
  [API_ENDPOINT.CMS_ARENA_STARTER_PACK]: {
    items: [
      { faceValueUsdt: "0.25", quantity: 4, expiryDurationMs: 86_400_000, sortOrder: 0 },
      { faceValueUsdt: "0.5", quantity: 2, expiryDurationMs: 86_400_000, sortOrder: 1 },
      { faceValueUsdt: "1", quantity: 1, expiryDurationMs: 86_400_000, sortOrder: 2 },
    ],
  },
  [API_ENDPOINT.CMS_ARENA_QUEST_REWARD]: {
    items: [{ faceValueUsdt: "5", quantity: 2, expiryDurationMs: 86_400_000, sortOrder: 0 }],
  },
  [API_ENDPOINT.CMS_ARENA_TICKET_CONFIGURATION]: {
    items: [
      { faceValueUsdt: "0.25", enabled: true, sortOrder: 0 },
      { faceValueUsdt: "0.5", enabled: true, sortOrder: 1 },
      { faceValueUsdt: "1", enabled: true, sortOrder: 2 },
    ],
  },
  [API_ENDPOINT.CMS_ARENA_TICKETS_GRANT]: { tickets: [] },
  [API_ENDPOINT.CMS_ARENA_TICKETS_FOR_USER]: { tickets: [] },
  [API_ENDPOINT.ARENA_AFFILIATE_APPLICATION_SUBMIT]: undefined,
  [API_ENDPOINT.CMS_ARENA_AFFILIATE_APPLICATIONS_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_AFFILIATE_APPLICATION_BY_ID]: MOCK_ARENA_AFFILIATE_APPLICATION,
  [API_ENDPOINT.CMS_ARENA_AFFILIATE_APPLICATION_SAVE_REVIEW]: MOCK_ARENA_AFFILIATE_APPLICATION,
  [API_ENDPOINT.BIG_ARENA_COMPETITION_MY_REGISTRATION]: { registration: null },
  [API_ENDPOINT.BIG_ARENA_COMPETITION_MY_REGISTRATION_SUBMIT]: {
    registration: {
      competition: "BIG_100K",
      status: "PENDING",
      acceptedVia: null,
      email: "trader@example.com",
      defiAddress: null,
      xHandle: null,
      telegramHandle: null,
      referralUtm: "bac_abcd1234",
      referralCount: 0,
      createdAt: "2026-09-10T09:00:00.000Z",
      updatedAt: "2026-09-10T09:00:00.000Z",
    },
  },
  [API_ENDPOINT.CMS_BIG_ARENA_COMPETITION_REGISTRATIONS_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_BIG_ARENA_COMPETITION_REGISTRATION_REVIEW]: MOCK_BIG_ARENA_COMPETITION_REGISTRATION,
  [API_ENDPOINT.CMS_BIG_ARENA_COMPETITION_PRIORITY_CODES_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_BIG_ARENA_COMPETITION_PRIORITY_CODES_GENERATE]: {
    items: [MOCK_BIG_ARENA_COMPETITION_PRIORITY_CODE],
  },
  [API_ENDPOINT.CMS_BIG_ARENA_COMPETITION_PRIORITY_CODE_UPDATE_LABEL]: MOCK_BIG_ARENA_COMPETITION_PRIORITY_CODE,
  [API_ENDPOINT.CMS_BIG_ARENA_COMPETITION_PRIORITY_CODE_DISABLE]: MOCK_BIG_ARENA_COMPETITION_PRIORITY_CODE,
  [API_ENDPOINT.ARENA_TOURNAMENT_ACTIVE]: { tournament: null },
  [API_ENDPOINT.ARENA_TOURNAMENT_CURRENT]: { slug: null },
  [API_ENDPOINT.ARENA_TOURNAMENT_GET]: MOCK_ARENA_TOURNAMENT,
  [API_ENDPOINT.ARENA_TOURNAMENT_ROUND_LEADERBOARD]: MOCK_ARENA_TOURNAMENT_ROUND_LEADERBOARD,
  [API_ENDPOINT.ARENA_TOURNAMENT_SNIPER_BOARD]: {
    board: "SNIPER",
    prizeUsdt: "400",
    minDuels: 15,
    items: [],
    total: 0,
    page: 0,
    size: 3,
  },
  [API_ENDPOINT.ARENA_TOURNAMENT_STREAK_BOARD]: {
    board: "STREAK",
    prizeUsdt: "400",
    items: [],
    total: 0,
    page: 0,
    size: 3,
  },
  [API_ENDPOINT.ARENA_TOURNAMENT_ME]: MOCK_ARENA_TOURNAMENT_ME,
  [API_ENDPOINT.ARENA_TOURNAMENT_JOIN]: MOCK_ARENA_TOURNAMENT_ME,
  [API_ENDPOINT.CMS_ARENA_TOURNAMENTS]: { items: [] },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_CREATE]: { id: 1 },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_BY_ID]: MOCK_CMS_ARENA_TOURNAMENT,
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_UPDATE]: { id: 1 },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_DELETE]: undefined,
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_ROUND_LEADERBOARD]: MOCK_ARENA_TOURNAMENT_ROUND_LEADERBOARD,
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_SNIPER_BOARD]: {
    board: "SNIPER",
    prizeUsdt: "400",
    minDuels: 15,
    items: [],
    total: 0,
    page: 0,
    size: 20,
  },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_STREAK_BOARD]: {
    board: "STREAK",
    prizeUsdt: "400",
    items: [],
    total: 0,
    page: 0,
    size: 20,
  },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_PARTICIPANTS]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_PARTICIPANT_DUELS]: { items: [] },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_VOIDED_DUELS]: { items: [] },
  [API_ENDPOINT.CMS_ARENA_TOURNAMENT_ACTION]: undefined,
  [API_ENDPOINT.CMS_ARENA_TICKET_LINK_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_TICKET_LINK_GET_BY_ID]: MOCK_CMS_ARENA_TICKET_LINK,
  [API_ENDPOINT.CMS_ARENA_TICKET_LINK_SAVE]: MOCK_CMS_ARENA_TICKET_LINK,
  [API_ENDPOINT.CMS_ARENA_TICKET_LINK_DELETE]: { message: "OK" },
  [API_ENDPOINT.CMS_ARENA_QUEUE_DISPLAY_SETTINGS]: {
    virtualReadyMin: 1,
    virtualReadyMax: 3,
    virtualWaitingMin: 2,
    virtualWaitingMax: 5,
    refreshHours: 4,
  },
  [API_ENDPOINT.CMS_ARENA_REENGAGEMENT_SETTINGS]: {
    enabled: false,
    recipientsPerPing: 5,
    criteria: "MOST_REGULAR",
    lookbackDays: 30,
    cooldownHours: 24,
    pingGraceMs: 10_000,
    ladderFallbackSteps: 2,
  },
  [API_ENDPOINT.CMS_ARENA_PRIVATE_COMPETITION_SETTINGS]: {
    rakeBps: 0,
  },
  [API_ENDPOINT.CMS_ARENA_PLATFORM_SETTINGS]: {
    rakeRecipientHubUserId: null,
    teamHubUserIds: [],
  },
  [API_ENDPOINT.CEDE_ARENA_REFERRALS]: {
    code: null,
    qualifyingVolumeUsdt: "0",
    unlockThresholdUsdt: "100",
    isUnlocked: false,
    totalRefereeVolumeUsdt: "0",
    pendingRewardsUsdt: "0",
    claimedRewardsUsdt: "0",
    tiers: [{ fromUsdt: "0", rateBps: 200 }],
    referrer: null,
    referees: [],
    nextCursor: null,
    canBeReferred: false,
  },
  [API_ENDPOINT.CEDE_ARENA_REFERRALS_CLAIM]: {
    code: null,
    qualifyingVolumeUsdt: "0",
    unlockThresholdUsdt: "100",
    isUnlocked: false,
    totalRefereeVolumeUsdt: "0",
    pendingRewardsUsdt: "0",
    claimedRewardsUsdt: "0",
    tiers: [{ fromUsdt: "0", rateBps: 200 }],
    referrer: null,
    referees: [],
    nextCursor: null,
    canBeReferred: false,
  },
  [API_ENDPOINT.CEDE_ARENA_REFERRALS_APPLY]: { referrer: null },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_STATUS]: { isKolManager: false, isKol: false },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER]: {
    code: null,
    maxShareBps: 7500,
    pendingRewardsUsdt: "0",
    claimedRewardsUsdt: "0",
    totals: { refereeVolumeUsdt: "0", rakeUsdt: "0", kolEarnedUsdt: "0", managerEarnedUsdt: "0" },
    kols: [],
    invites: [],
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_CLAIM]: {
    code: null,
    maxShareBps: 7500,
    pendingRewardsUsdt: "0",
    claimedRewardsUsdt: "0",
    totals: { refereeVolumeUsdt: "0", rakeUsdt: "0", kolEarnedUsdt: "0", managerEarnedUsdt: "0" },
    kols: [],
    invites: [],
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_VOLUME]: { points: [] },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_KOLS]: { kolHubUserId: 1 },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_KOL_DETAIL]: {
    referees: [],
    history: [],
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_KOL_SPLIT]: { kolHubUserId: 1 },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_INVITES]: {
    token: "mock",
    email: "kol@example.com",
    status: "ACTIVE",
    kolShareBps: 5000,
    managerShareBps: 2500,
    createdAt: new Date(0),
    expiresAt: new Date(0),
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_INVITE]: {
    kolShareBps: 5000,
    managerShareBps: 2500,
    managerNickname: null,
    email: "kol@example.com",
    status: "ACTIVE",
    expiresAt: new Date(0),
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_INVITE_ACCEPT]: {
    kolShareBps: 5000,
    managerShareBps: 2500,
    managerNickname: null,
    email: null,
    status: "CONSUMED",
    expiresAt: new Date(0),
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_ROLE_INVITE]: {
    email: "agency@example.com",
    status: "ACTIVE",
    expiresAt: new Date(0),
  },
  [API_ENDPOINT.CEDE_ARENA_KOL_MANAGER_ROLE_INVITE_ACCEPT]: {
    email: null,
    status: "CONSUMED",
    expiresAt: new Date(0),
  },
  [API_ENDPOINT.CMS_ARENA_REFERRAL_SETTINGS]: {
    unlockThresholdUsdt: "100",
    tiers: [
      { fromUsdt: "0", rateBps: 200 },
      { fromUsdt: "100", rateBps: 300 },
      { fromUsdt: "300", rateBps: 400 },
    ],
    rewardExpiryDays: null,
    managerMaxShareBps: 7500,
  },
  [API_ENDPOINT.CMS_ARENA_REFERRERS_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_REFEREES_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 50 },
  [API_ENDPOINT.CMS_ARENA_REFERRAL_OVERRIDES]: { hubUserId: 1 },
  [API_ENDPOINT.CMS_ARENA_REFERRAL_OVERRIDES_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGERS]: { hubUserId: 1 },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGERS_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGER_KOLS]: { items: [] },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGER_KOLS_UNLINK]: { kolHubUserId: 1 },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGER_KOLS_REASSIGN]: { kolHubUserId: 1 },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGER_EVENTS]: { items: [], total: 0, page: 0, size: 50 },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGER_ROLE_INVITES]: {
    token: "token",
    email: "agency@example.com",
    note: null,
    status: "ACTIVE",
    createdAt: new Date(0),
    expiresAt: new Date(0),
    consumedAt: null,
    consumedByHubUserId: null,
    consumedByNickname: null,
  },
  [API_ENDPOINT.CMS_ARENA_KOL_MANAGER_ROLE_INVITES_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CEDE_ARENA_CLANS]: { clans: [] },
  [API_ENDPOINT.CEDE_ARENA_CLAN_BADGES]: { badges: [] },
  [API_ENDPOINT.CEDE_ARENA_CLAN_ME]: { membership: null, rejoinAvailableAt: null, rejoinCooldownHours: 24 },
  [API_ENDPOINT.CEDE_ARENA_CLAN_LEAVE]: { membership: null, rejoinAvailableAt: null, rejoinCooldownHours: 24 },
  [API_ENDPOINT.CEDE_ARENA_CLAN_JOIN]: { membership: null, rejoinAvailableAt: null, rejoinCooldownHours: 24 },
  [API_ENDPOINT.CEDE_ARENA_CLAN_INVITE_LINK]: { joinCode: null },
  [API_ENDPOINT.CEDE_ARENA_CLAN_BY_SLUG]: {
    id: 1,
    slug: "night-shift",
    name: "Night Shift",
    logoUrl: null,
    isPrivate: false,
    memberCount: 0,
    maxMembers: 50,
    chief: null,
    hasOngoingTournament: false,
    members: [],
    tournament: null,
  },
  [API_ENDPOINT.CMS_ARENA_CLAN_SETTINGS]: { rejoinCooldownHours: 24 },
  [API_ENDPOINT.CMS_ARENA_CLANS_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_CLAN_GET_BY_ID]: MOCK_CMS_ARENA_CLAN,
  [API_ENDPOINT.CMS_ARENA_CLAN_SAVE]: MOCK_CMS_ARENA_CLAN,
  [API_ENDPOINT.CMS_ARENA_CLAN_MEMBERS_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 50 },
  [API_ENDPOINT.CMS_ARENA_CLAN_ASSIGN_CHIEF]: MOCK_CMS_ARENA_CLAN,
  [API_ENDPOINT.CMS_ARENA_CLAN_REMOVE_MEMBER]: MOCK_CMS_ARENA_CLAN,
  [API_ENDPOINT.CMS_ARENA_CLAN_TOURNAMENTS_GET_BY_PAGE]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_CLAN_TOURNAMENT_GET_BY_ID]: MOCK_CMS_ARENA_CLAN_TOURNAMENT,
  [API_ENDPOINT.CMS_ARENA_CLAN_TOURNAMENT_SAVE]: MOCK_CMS_ARENA_CLAN_TOURNAMENT,
  [API_ENDPOINT.CMS_ARENA_SURVEY_RESPONSES]: { items: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CMS_ARENA_SURVEY_SETTINGS]: {
    questions: [
      { id: "enjoyment", text: "How much did you enjoy the competition?" },
      { id: "trade_ease", text: "How easy was it to trade?" },
      { id: "compete_again", text: "How likely are you to compete again?" },
    ],
  },
  [API_ENDPOINT.CMS_ARENA_QUALIFICATION_SETTINGS]: { questions: MOCK_ARENA_QUALIFICATION_QUESTIONS },
  [API_ENDPOINT.CMS_ARENA_EVENT_SETTINGS]: {
    eventKey: "comp-1",
    title: "Cede Arena",
    description: "Buy in, trade, winner takes all.",
    location: "",
    startsAt: "2026-06-02T18:00:00.000Z",
    endsAt: "2026-06-02T21:00:00.000Z",
    targetUrl: "https://arena.cede.store",
    responseMode: "CALENDAR",
  },
  [API_ENDPOINT.ADMIN_CEDE_ARENA_BLUEPRINTS]: {} as GetCedeArenaCompetitionBlueprintResponse,
  [API_ENDPOINT.ADMIN_CEDE_ARENA_BLUEPRINT_PREVIEWS]: { blueprints: [] },
  [API_ENDPOINT.ADMIN_CEDE_ARENA_BLUEPRINT_BY_ID]: {} as GetCedeArenaCompetitionBlueprintResponse,
  [API_ENDPOINT.ADMIN_CEDE_ARENA_UNLINKED_COMPETITIONS]: { competitions: [] },
  [API_ENDPOINT.ADMIN_CEDE_ARENA_COMPETITIONS]: { competitions: [] },
  [API_ENDPOINT.ADMIN_CEDE_ARENA_WHITELIST]: { items: [], total: 0, page: 1, limit: 20, totalPages: 0 },
  [API_ENDPOINT.CMS_EXPERIMENTS]: [],
  [API_ENDPOINT.CMS_CANBERRAI_PLAYER_DIFFICULTY]: {
    resolvedBy: null,
    player: null,
    decisions: [],
    hasMore: false,
    loop: {
      windowDuels: 10,
      replayDuels: 30,
      gain: 5,
      targetWinRateMin: 0.45,
      targetWinRateMax: 0.55,
      enterAt: 0.5,
      holdAt: 0.1,
    },
  },
  [API_ENDPOINT.CMS_CANBERRAI_CONFIG]: {
    botHubUserIds: [],
    duelStartDelayMs: 0,
    readyCheckTimeoutMs: 30_000,
    autoAcceptReadyCheckTimeoutMs: 3000,
    rematchCooldownMs: 10_000,
    rematchProposeWindowMs: 60_000,
    botRematchMaxConsecutiveLosses: 3,
    botRematchMinConsecutiveLosses: 3,
    botRematchMaxLossesInWindow: 8,
    botRematchMinLossesInWindow: 8,
    botRematchLossWindowMs: 86_400_000,
    botDuelMaxLifetimeCap: 0,
    botDuelMinLifetimeCap: 0,
    botWaitLadder: [{ fromPaidDuels: 0, extraMinMs: 0, extraMaxMs: 0 }],
    botWaitLadderWindowDays: 30,
    queueTtlMs: 3_600_000,
    entryIntervalMs: 250,
    leadStandDownFraction: 0,
    bots: [],
    buyInUsdt: "10",
    autoMatchEnabled: false,
  },
  [API_ENDPOINT.CMS_ARENA_BOT_SCHEDULER_CONFIG]: {
    targetCompetitionsPerDay: 0,
    hourlyWeights: [],
    botCompetitionsEnabled: false,
  },
  [API_ENDPOINT.CMS_ARENA_TRAFFIC_CURVE_CONFIG]: {
    enabled: false,
    mode: "HISTORICAL",
    lookbackDays: 30,
    minSampleSize: 200,
    manualPeakHourUtc: 13,
    manualSigmaHours: 2.5,
    showLiveQueue: true,
    historicalFit: null,
  },
  [API_ENDPOINT.CMS_ARENA_MATCHMAKING_CONFIG]: {
    configs: [
      {
        tier: "FREE",
        durationMs: 1_800_000,
        entryFeeUsdt: 0,
        buyIns: [],
        pairSymbols: ["BTC/USDC:USDC"],
        hotPairsCount: 0,
        nextBotDelayMinMs: 10_000,
        nextBotDelayMaxMs: 30_000,
        minVolumeUsd: "0",
        initialDeposit: 10_000,
        rakeBps: 0,
        botCutoffMinMs: 60_000,
        botCutoffMaxMs: 180_000,
        displayedEstimatedWaitMs: 120_000,
        downgradeCutoffMs: 90_000,
        botHubUserIds: [],
      },
      {
        tier: "PAID",
        durationMs: 1_800_000,
        entryFeeUsdt: 10,
        buyIns: [
          {
            amountUsdt: "1",
            enabled: true,
            canberraiEligible: true,
            autopilotPriorityTickets: 0,
            ...ARENA_DEFAULT_BUY_IN_TIMING,
            botHubUserIds: [],
          },
          {
            amountUsdt: "5",
            enabled: true,
            canberraiEligible: true,
            autopilotPriorityTickets: 0,
            ...ARENA_DEFAULT_BUY_IN_TIMING,
            botHubUserIds: [],
          },
          {
            amountUsdt: "10",
            enabled: true,
            canberraiEligible: true,
            autopilotPriorityTickets: 0,
            ...ARENA_DEFAULT_BUY_IN_TIMING,
            botHubUserIds: [],
          },
          {
            amountUsdt: "50",
            enabled: true,
            canberraiEligible: false,
            autopilotPriorityTickets: 0,
            ...ARENA_DEFAULT_BUY_IN_TIMING,
            botHubUserIds: [],
          },
        ],
        pairSymbols: ["BTC/USDC:USDC"],
        hotPairsCount: 0,
        nextBotDelayMinMs: 10_000,
        nextBotDelayMaxMs: 30_000,
        minVolumeUsd: "0",
        initialDeposit: 10_000,
        rakeBps: 0,
        botCutoffMinMs: 60_000,
        botCutoffMaxMs: 180_000,
        displayedEstimatedWaitMs: 120_000,
        downgradeCutoffMs: 90_000,
        botHubUserIds: [],
      },
    ],
  },
  // Attribution PUT answers with the same all-tiers read payload.
  [API_ENDPOINT.CMS_ARENA_BOT_ATTRIBUTIONS]: { configs: [] },
  [API_ENDPOINT.CMS_SPIN_AND_GO_CONFIG]: {
    configs: [
      {
        category: "QUICK_MATCH_1V1",
        enabled: false,
        oddsTable: [],
        lowWatermark: 20,
        availableCount: 0,
        lowWatermarkNotifiedAt: null,
      },
      {
        category: "PRIVATE_ROOM",
        enabled: false,
        oddsTable: [],
        lowWatermark: 20,
        availableCount: 0,
        lowWatermarkNotifiedAt: null,
      },
      {
        category: "OPT_IN",
        enabled: false,
        oddsTable: [],
        lowWatermark: 20,
        availableCount: 0,
        lowWatermarkNotifiedAt: null,
      },
    ],
  },
  [API_ENDPOINT.CMS_SPIN_AND_GO_MULTIPLIERS]: {
    category: "QUICK_MATCH_1V1",
    rows: [],
    availableCount: 0,
    realizedAvgFactor: "0",
    worstCaseSubsidyFactor: "0",
  },
  [API_ENDPOINT.CMS_SPIN_AND_GO_MULTIPLIERS_VOID]: {
    category: "QUICK_MATCH_1V1",
    rows: [],
    availableCount: 0,
    realizedAvgFactor: "0",
    worstCaseSubsidyFactor: "0",
  },
  [API_ENDPOINT.SPIN_AND_GO_PUBLIC_CONFIG]: {
    enabled: false,
    available: 0,
    oddsTable: [],
  },
  [API_ENDPOINT.CEDE_ARENA_MATCHMAKING_TIERS]: {
    tiers: [
      {
        tier: "FREE",
        durationMs: 1_800_000,
        entryFeeUsdt: 0,
        buyIns: [],
        pairSymbols: ["BTC/USDC:USDC"],
        hotPairsCount: 0,
        minVolumeUsd: "0",
        initialDeposit: 10_000,
        rakeBps: 0,
      },
      {
        tier: "PAID",
        durationMs: 1_800_000,
        entryFeeUsdt: 10,
        // Public shape: enabled amounts + ticket award only — never carries bot eligibility.
        buyIns: [
          { amountUsdt: "1", autopilotPriorityTickets: 0 },
          { amountUsdt: "5", autopilotPriorityTickets: 0 },
          { amountUsdt: "10", autopilotPriorityTickets: 0 },
          { amountUsdt: "50", autopilotPriorityTickets: 0 },
        ],
        pairSymbols: ["BTC/USDC:USDC"],
        hotPairsCount: 0,
        minVolumeUsd: "0",
        initialDeposit: 10_000,
        rakeBps: 0,
      },
    ],
  },
  [API_ENDPOINT.HUB_LEADERBOARD_SIMULATE]: {
    records: [],
    simulationData: {
      weightByVolume: [],
      allUsersWeight: 0,
      weightsByUser: {},
    },
    rule: "gaussian",
  } as LeaderboardSimulateResponse,
  [API_ENDPOINT.HUB_LEADERBOARD_CAMPAIGNS]: ["test-campaign", "campaign-1", "campaign-2"],
  [API_ENDPOINT.HUB_LEADERBOARD_SHOULD_DISABLE_TRADE]: { shouldDisableTrade: false },
  [API_ENDPOINT.HUB_LEADERBOARD_MY_EPOCHS]: { epochIds: [] },
  [API_ENDPOINT.SPINNING_WHEEL_TMA_ELIGIBILITY]: {
    isEligible: true,
    questId: 66,
  },
  // Main Spinning Wheel mocks
  [API_ENDPOINT.SPINNING_WHEEL_REWARDS]: {
    rewards: [
      { rewardAmount: new Decimal("0.5"), rewardSymbol: "USDT" },
      { rewardAmount: new Decimal("1"), rewardSymbol: "USDT" },
      { rewardAmount: new Decimal("10"), rewardSymbol: "USDT" },
      { rewardAmount: new Decimal("50"), rewardSymbol: "USDT" },
      { rewardAmount: new Decimal("1"), rewardSymbol: "ETH" },
      { rewardAmount: new Decimal("1000"), rewardSymbol: "USDT" },
    ],
  },
  [API_ENDPOINT.SPINNING_WHEEL_TICKETS]: {
    totalTickets: 5,
  },
  [API_ENDPOINT.SPINNING_WHEEL_RESULTS]: {
    results: [],
  },
  [API_ENDPOINT.SPINNING_WHEEL_THRESHOLD_PROGRESS]: {
    currentUsdValue: new Decimal("1.25"),
    thresholdUsdValue: new Decimal("2.0"),
    isThresholdReached: false,
    progressPercentage: 62.5,
    pendingResultsCount: 3,
  },
  [API_ENDPOINT.SPINNING_WHEEL_SPIN]: undefined,
  [API_ENDPOINT.SPINNING_WHEEL_RECENT_WINNERS]: [
    {
      defiAddress: "0x1234...5678",
      rewardAmount: new Decimal("10"),
      rewardSymbol: "USDT",
    },
    {
      defiAddress: "0xabcd...ef01",
      rewardAmount: new Decimal("1"),
      rewardSymbol: "ETH",
    },
  ],
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_PARADEX]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_GRVT_BUILDER_PREPARE]: {
    prepareId: "00000000-0000-0000-0000-000000000000",
    eip712TypedData: {
      types: {
        EIP712Domain: [{ name: "name", type: "string" }],
        AddAccountSignerWithBuilder: [{ name: "accountID", type: "address" }],
      },
      primaryType: "AddAccountSignerWithBuilder",
      domain: { name: "GRVT Exchange", version: "0", chainId: 325 },
      message: {},
    },
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_GRVT_BUILDER_AUTHORIZE]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ZEROONE_PREPARE]: {
    prepareId: "00000000-0000-0000-0000-000000000001",
    messageToSign: "Authorize CedeHub to trade on 01.xyz on your behalf",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ZEROONE_AUTHORIZE]: {
    accountId: 1,
    walletAddress: "MockSolanaAddress1234567890123456789012345",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_EXTENDED_PREPARE]: {
    prepareId: "mock-prepare-id",
    messageToSign: "/api/v1/user/account/api-key@2026-01-01T00:00:00Z",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_EXTENDED_AUTHORIZE]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_HIBACHI]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_CONNECT_HIBACHI_WALLET]: {
    isFinal: false,
    prepareId: "00000000-0000-4000-8000-000000000000",
    remainingGrants: 1,
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_CONNECT_HIBACHI_WALLET_TOTP]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_CONNECT_HIBACHI_WALLET_GRANT]: {
    isFinal: true,
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_DECIBEL]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_DECIBEL_DELEGATION_PREPARE]: {
    prepareId: "00000000-0000-0000-0000-000000000000",
    transactions: [
      {
        kind: "DELEGATION" as const,
        transactionBytesHex: "00".repeat(64),
        transactionPayload: {
          function:
            "0x50ead22afd6ffd9769e3b3d6e0e64a2a350d68e8b102c4e72e33d0b8cfdfdb06::dex_accounts_entry::delegate_trading_to_for_subaccount",
          typeArguments: [] as string[],
          functionArguments: ["0x" + "ab".repeat(32), "0x" + "ef".repeat(32), 1893456000] as unknown[],
        },
      },
      {
        kind: "BUILDER_FEE_APPROVAL" as const,
        transactionBytesHex: "00".repeat(64),
        transactionPayload: {
          function:
            "0x50ead22afd6ffd9769e3b3d6e0e64a2a350d68e8b102c4e72e33d0b8cfdfdb06::dex_accounts_entry::approve_max_builder_fee_for_subaccount",
          typeArguments: [] as string[],
          functionArguments: ["0x" + "ab".repeat(32), "0x" + "cd".repeat(32), 1] as unknown[],
        },
      },
    ],
    withFeePayer: true as const,
    subaccountAddress: "0x" + "ab".repeat(32),
    aptosPrimary: "0x" + "cd".repeat(32),
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_DECIBEL_DELEGATION_AUTHORIZE]: {
    isFinal: true,
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_RISE_PREPARE]: {
    prepareId: "00000000-0000-0000-0000-000000000002",
    agentAddress: "0x1234567890123456789012345678901234567890",
    eip712TypedData: {
      types: {
        EIP712Domain: [{ name: "name", type: "string" }],
        RegisterSigner: [{ name: "account", type: "address" }],
      },
      primaryType: "RegisterSigner",
      domain: {
        name: "RISEx",
        version: "1",
        chainId: 4153,
        verifyingContract: "0x0D919DAA3f12AE715744Eb648c00066c5DBd66f0",
      },
      message: {},
    },
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_RISE_AUTHORIZE]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_HYPERLIQUID]: {
    accountId: 1,
    agentAddress: "0x1234567890123456789012345678901234567890",
    agentName: "Hyperliquid 1",
    status: "waitingApproval",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_APPROVE_HYPERLIQUID]: {
    status: "waitingDeposit",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_ADD_HOTSTUFF]: (() => {
    // Nonce + validUntil must be relative to `now`. Fixed 2024 timestamps
    // would be rejected by the addAgent expiry guard as already-stale.
    const nonce = Date.now();
    return {
      agentAddress: "0x1234567890123456789012345678901234567890",
      agentName: "Hotstuff 1",
      nonce,
      validUntil: nonce + 30 * 24 * 60 * 60 * 1000,
      actionHash: "0x0000000000000000000000000000000000000000000000000000000000000000",
      prepareId: "00000000-0000-0000-0000-00000000c0de",
      status: "waitingApproval",
    };
  })(),
  [API_ENDPOINT.EXCHANGE_ACCOUNT_APPROVE_HOTSTUFF]: {
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_KATANA_DELEGATION_PREPARE]: {
    prepareId: "00000000-0000-1000-8000-000000000000",
    eip712TypedData: {
      types: {
        DelegatedKeyAuthorization: [
          { name: "nonce", type: "uint128" },
          { name: "delegatedPublicKey", type: "address" },
          { name: "message", type: "string" },
        ],
      },
      primaryType: "DelegatedKeyAuthorization" as const,
      domain: {
        name: "KatanaPerps" as const,
        version: "2.0.0",
        chainId: 747474,
        verifyingContract: "0x62230CeA619F734cc215bB8074bbF07bE4Eb633e",
      },
      message: {
        nonce: "1",
        delegatedPublicKey: "0x1111111111111111111111111111111111111111",
        message: "Sign this free message to prove you control this wallet" as const,
      },
    },
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_KATANA_DELEGATION_AUTHORIZE]: {
    accountId: 1,
    walletAddress: "0x1234567890123456789012345678901234567890",
    status: "online",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_HYPERLIQUID_STATUS]: {
    status: "waitingDeposit",
  },
  [API_ENDPOINT.EXCHANGE_ACCOUNT_HYPERLIQUID_ELIGIBILITY]: {
    eligible: false,
    reason: "Already referred",
  },
  [API_ENDPOINT.HOLD_TO_EARN_LEADERBOARD]: [
    {
      id: "550e8400-e29b-41d4-a716-446655440001",
      hubUserId: 12345,
      rank: 1,
      tokenAmount: "1000.50",
      tokenAmountAverage: "1000.50",
      tokenSymbol: "BTC",
      rewardTokenAmount: "100.0",
      rewardTokenSymbol: "USDT",
      rewardTokenAddress: "0x1234567890123456789012345678901234567890",
      hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
      rewardClaimed: false,
      rewardClaimedAt: null,
      createdAt: new Date("2024-01-01T00:00:00Z"),
      updatedAt: new Date("2024-01-01T00:00:00Z"),
      isTopHolder: false,
      isAmbassador: false,
    },
    {
      id: "550e8400-e29b-41d4-a716-446655440002",
      hubUserId: 12346,
      rank: 2,
      tokenAmount: "750.25",
      tokenAmountAverage: "750.25",
      tokenSymbol: "BTC",
      rewardTokenAmount: "75.0",
      rewardTokenSymbol: "USDT",
      rewardTokenAddress: "0x1234567890123456789012345678901234567890",
      hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
      rewardClaimed: false,
      rewardClaimedAt: null,
      createdAt: new Date("2024-01-01T00:00:00Z"),
      updatedAt: new Date("2024-01-01T00:00:00Z"),
      isTopHolder: false,
      isAmbassador: false,
    },
    {
      id: "550e8400-e29b-41d4-a716-446655440003",
      hubUserId: 12347,
      rank: 3,
      tokenAmount: "500.00",
      tokenAmountAverage: "500.00",
      tokenSymbol: "BTC",
      rewardTokenAmount: "50.0",
      rewardTokenSymbol: "USDT",
      rewardTokenAddress: "0x1234567890123456789012345678901234567890",
      hteLeaderboardId: "550e8400-e29b-41d4-a716-446655440000",
      rewardClaimed: false,
      rewardClaimedAt: null,
      createdAt: new Date("2024-01-01T00:00:00Z"),
      updatedAt: new Date("2024-01-01T00:00:00Z"),
      isTopHolder: false,
      isAmbassador: false,
    },
  ],
  [API_ENDPOINT.HOLD_TO_EARN_PARTICIPANT_STATUS]: {
    id: "550e8400-e29b-41d4-a716-446655440100",
    campaignId: "1",
    hubUserId: 12345,
    optedOut: false,
    optedOutAtEpoch: null,
  },
  [API_ENDPOINT.HUB_MAINTENANCE_STATUS]: {
    isMaintenanceMode: false,
    message: "Application is operational",
  },
  [API_ENDPOINT.HUB_FEATURE_FLAGS]: {},
  [API_ENDPOINT.HUB_FEATURE_FLAGS_V2]: {},
  [API_ENDPOINT.CEDE_ARENA_RECENT_WINS]: { wins: [], totalPaidUsdt: "0" },
  [API_ENDPOINT.HUB_SOCIALS_ACCOUNTS]: [
    {
      id: 1,
      socialName: SOCIAL_NETWORK.TELEGRAM,
      userId: "1234567890",
      username: "test",
      avatarUrl: "https://example.com/avatar.png",
      status: SOCIAL_STATUS.ONLINE,
      createdAt: new Date(),
    },
  ],
  [API_ENDPOINT.HUB_VOUCHER_REDEEM]: undefined,
  [API_ENDPOINT.ADMIN_MONITORING_CAMPAIGNS]: {
    campaigns: [
      {
        slug: "kucoin-usdt-ton",
        name: "KuCoin TON/USDT Campaign",
        rewardVaultAddress: "0x1234567890abcdef",
        status: "live" as const,
        latestEpochId: 5,
      },
      {
        slug: "binance-usdt-btc",
        name: "Binance BTC/USDT Campaign",
        rewardVaultAddress: "0xabcdef1234567890",
        status: "ended" as const,
        endedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        latestEpochId: 10,
      },
    ],
    totalCount: 2,
  },
  [API_ENDPOINT.ADMIN_MONITORING_CAMPAIGN_HEALTH]: {
    health: {
      campaignSlug: "kucoin-usdt-ton",
      rewardVaultAddress: "0x1234567890abcdef",
      isHealthy: false,
      lastCheckedAt: new Date().toISOString(),
      issues: [
        {
          type: IssueType.MISSING_COOLDOWN_END,
          severity: IssueSeverity.ERROR,
          message: "Epoch 5 finished 25 minutes ago but cooldownEnd is not set",
          details: {
            epochId: 5,
            epochEnd: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
            rewardVaultAddress: "0x1234567890abcdef",
            campaignSlug: "kucoin-usdt-ton",
          },
        },
        {
          type: IssueType.S3_DATABASE_MISMATCH,
          severity: IssueSeverity.ERROR,
          message: "S3 missing 2 addresses that exist in database",
          details: {
            epochId: 4,
            epochEnd: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            rewardVaultAddress: "0x1234567890abcdef",
            campaignSlug: "kucoin-usdt-ton",
            databaseCount: 100,
            s3Count: 98,
            missingAddresses: ["0xabc123", "0xdef456"],
          },
        },
      ],
    },
  },
  [API_ENDPOINT.HALL_OF_FAME_SEASONS]: [
    {
      id: "550e8400-e29b-41d4-a716-446655440000",
      index: 1,
      name: "Season 1",
      startDate: new Date("2024-01-01T00:00:00Z"),
      endDate: new Date("2024-01-01T00:00:00Z"),
    },
  ],
  [API_ENDPOINT.HALL_OF_FAME_TOP_50]: [
    {
      rank: 1,
      defiAddress: "0x1234567890abcdef",
      tradedVolumeUSD: new Decimal("1000.00"),
      isAmbassador: false,
    },
    {
      rank: 2,
      defiAddress: "0x1234567890abcdef",
      tradedVolumeUSD: new Decimal("1000.00"),
      isAmbassador: false,
    },
  ],
  [API_ENDPOINT.HALL_OF_FAME_USER_RANK_DATA]: {
    userRank: 1,
    tradedVolumeUSD: new Decimal("1000.00"),
    totalParticipants: 100,
    top50: [
      {
        rank: 1,
        defiAddress: "0x1234567890abcdef",
        tradedVolumeUSD: new Decimal("1000.00"),
        isAmbassador: false,
      },
    ],
  },
  [API_ENDPOINT.HALL_OF_FAME_COUNTDOWN_REWARDS]: {
    rewards: [
      { day: 15, rewardType: "discord", rewardLabel: "Discord Role" },
      { day: 16, rewardType: "discord", rewardLabel: "Discord Role" },
    ],
    currentDay: 17,
  },

  // Onchain Swap endpoints
  [API_ENDPOINT.ONCHAIN_SWAP_QUOTE]: {
    data: {
      amountOutTokens: "1000.5",
      amountInUSD: 1000,
      amountOutUSD: 1005,
      marketImpactPercentage: 0.1,
      tokenIn: {
        address: "0x0000000000000000000000000000000000000000",
        name: "Ethereum",
        symbol: "ETH",
        decimals: 18,
        logo: "https://example.com/eth.png",
      },
      tokenOut: {
        address: "0xdac17f958d2ee523a2206206994597c13d831ec7",
        name: "Tether USD",
        symbol: "USDT",
        decimals: 6,
        logo: "https://example.com/usdt.png",
      },
      requestId: "test-request-id",
    },
  },

  [API_ENDPOINT.ONCHAIN_SWAP_PORTFOLIO]: {
    data: {
      total_wallet_balance: 10000,
      wallets: ["0x1234567890abcdef"],
      assets: [
        {
          asset: {
            id: 1,
            name: "Ethereum",
            symbol: "ETH",
            decimals: ["18"],
            contracts: ["0x0000000000000000000000000000000000000000"],
            blockchains: ["Ethereum"],
            logo: "https://example.com/eth.png",
          },
          token_balance: 5,
          price: 2000,
          estimated_balance: 10000,
          contracts_balances: [
            {
              address: "0x0000000000000000000000000000000000000000",
              balance: 5,
              balanceRaw: "5000000000000000000",
              chainId: "1",
              decimals: 18,
            },
          ],
        },
      ],
      balances_length: 1,
    },
  },

  [API_ENDPOINT.ONCHAIN_SWAP_TOKEN_BY_ADDRESS]: {
    address: "0xdac17f958d2ee523a2206206994597c13d831ec7",
    symbol: "USDT",
    name: "Tether USD",
    decimals: 6,
    tokenSymbol: "USDT",
    tokenName: "Tether USD",
    market_cap: 100000000000,
    volume_24h: 50000000000,
    latest_price: 1,
    logo: "https://example.com/usdt.png",
  },

  [API_ENDPOINT.ONCHAIN_SWAP_TOKENS_FOR_SWAP]: {
    tokens: [
      {
        address: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
        symbol: "ETH",
        name: "Ethereum",
        decimals: 18,
        chainId: "8453",
        logo: "https://example.com/eth.png",
        balance: "5000000000000000000",
        balanceFormatted: "5.0",
        marketCap: 250000000000,
      },
    ],
    metadata: {
      totalCount: 1,
      walletTokensCount: 1,
      popularTokensCount: 0,
      deduplicatedCount: 0,
      chainId: "8453",
    },
  },

  [API_ENDPOINT.ONCHAIN_SWAP_DEX_PAIRS]: {
    pairs: [
      {
        pairAddress: "0x1234567890abcdef1234567890abcdef12345678",
        token0: {
          address: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
          symbol: "ETH",
          name: "Ethereum",
          decimals: 18,
          logo: "https://example.com/eth.png",
        },
        token1: {
          address: "0xdac17f958d2ee523a2206206994597c13d831ec7",
          symbol: "USDC",
          name: "USD Coin",
          decimals: 6,
          logo: "https://example.com/usdc.png",
        },
        volume24h: 5000000,
      },
      {
        pairAddress: "0xabcdef1234567890abcdef1234567890abcdef12",
        token0: {
          address: "0x0000000000000000000000000000000000000001",
          symbol: "BTC",
          name: "Bitcoin",
          decimals: 8,
          logo: "https://example.com/btc.png",
        },
        token1: {
          address: "0xdac17f958d2ee523a2206206994597c13d831ec7",
          symbol: "USDC",
          name: "USD Coin",
          decimals: 6,
          logo: "https://example.com/usdc.png",
        },
        volume24h: 10000000,
      },
    ],
    metadata: {
      totalCount: 2,
      chainId: "8453",
    },
  },

  [API_ENDPOINT.ONCHAIN_SWAP_SUBMIT_TRANSACTION]: {
    success: true,
    jobId: "index-onchain-tx-0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef-8453",
    txHash: "0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef",
    chainId: "8453",
  },
  [API_ENDPOINT.ONCHAIN_SWAP_WALLET_AGE_ELIGIBILITY]: {
    isEligible: true,
    checkedAt: Date.now(),
    reason: null,
    eligibleAfter: null,
  },
  [API_ENDPOINT.ONCHAIN_SWAP_SYSTEM_METADATA]: {
    poolTypes: ["uniswap-v3", "uniswap-v2", "pancakeswap-v3"],
    factories: [
      { name: "uniswap-v3", uiName: "Uniswap V3", logo: "https://example.com/uniswap.png" },
      { name: "pancakeswap-v3", uiName: "PancakeSwap V3", logo: "https://example.com/pancakeswap.png" },
    ],
    chains: [
      { name: "Ethereum", chainId: "1", logo: "https://example.com/ethereum.png" },
      { name: "Base", chainId: "8453", logo: "https://example.com/base.png" },
    ],
  },
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_FULL_DATA_BY_NAME]: {
    id: "1",
    name: "Test Campaign",
    chainId: 42161,
    rewardVaultAddress: "0x1234567890abcdef",
    status: "live" as const,
    title: "Test Campaign",
    tokenSymbol: "TEST",
    prizePoolToken: "TEST",
    updatedAt: new Date(),
    createdAt: new Date(),
    epochGroups: [],
    slippageThreshold: 0,
    maxTradeAmountUSD: 0,
    campaignPartners: [],
    prizePool: "",
    publicationStatus: "draft" as const,
    exchangeIds: [],
    visibility: "public" as const,
    privateCampaignPartner: "",
    uniqueParticipationByUser: false,
    privateCampaignUtms: [],
    startedAt: new Date(),
    endedAt: new Date(),
    production: true,
    networkReward: [],
    descriptions: [],
    icon: "https://example.com/icon.png",
    poolAddress: "0x1234567890abcdef",
    dexTokenAddress: "0x1234567890abcdef",
    requiredSocialAccounts: [],
    authorizedParticipants: [],
    exclusiveOptInParticipants: [],
    epochs: [],
    nextEpoch: null,
    currentEpoch: null,
    isNew: false,
    currentEpochReward: "0",
    pairSymbols: [],
    spreadThresholdAlert: 2,
    autopilotSpreadThreshold: 2,
    autopilotCaptchaEnabled: false,
    disableHyperliquidRefereeCheck: false,
    winnerHubUserId: null,
    winnerNickname: null,
    didCurrentUserWin: false,
  },
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_LIVE_EPOCHS]: [],
  [API_ENDPOINT.ADMIN_TRADE_TO_EARN_CAMPAIGN_VOLUME_TARGETS]: [],
  [API_ENDPOINT.ADMIN_REWARD_TOKEN_METADATA]: {
    chainId: 42161,
    address: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
    symbol: "USDT",
    decimals: 6,
  },
  [API_ENDPOINT.ADMIN_REWARD_TOKEN_DEFAULT]: {
    chainId: 42161,
    address: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9",
    symbol: "USDT",
    decimals: 6,
  },
  [API_ENDPOINT.ADMIN_CAMPAIGN_PARTNERS]: {
    partners: [],
    totalCount: 0,
    page: 1,
  },
  [API_ENDPOINT.ADMIN_CAMPAIGN_PARTNER_BY_ID]: {
    id: "1",
    name: "Test Campaign Partner",
    description: "Test Campaign Partner Description",
    socials: [],
    createdAt: new Date(),
    updatedAt: new Date(),
    marketCap: "100000000000",
    allTimeLow: "100000000000",
    allTimeHigh: "100000000000",
    websiteUrl: "https://example.com",
    coinGeckoUrl: "https://example.com/coin-gecko.png",
  },
  [API_ENDPOINT.ADMIN_SOLO_CMS_FILES]: {
    files: [],
    totalCount: 0,
  },
  [API_ENDPOINT.ADMIN_SOLO_CMS_UPLOAD]: {
    key: "test/file.png",
  },

  // Cashback endpoints
  [API_ENDPOINT.CASHBACK_STATUS]: {
    accumulated: {
      amount: "12.50",
      currency: "USDT",
    },
    awaitingDistribution: null,
    claimable: null,
    claimableSoon: null,
    monthlyUsage: {
      used: "12.50",
      claimed: "0.00",
      cap: "500.00",
      remaining: "487.50",
      month: "2025-01",
    },
    unlockProgress: {
      current: "12.50",
      threshold: "5.00",
      percentage: 100,
      pendingTransactions: [],
    },
  },
  [API_ENDPOINT.CASHBACK_CONFIG]: {
    cashbackRate: new Decimal("0.25"),
    unlockThresholdUsd: new Decimal("5.00"),
    monthlyCapUsd: new Decimal("500.00"),
    isActive: true,
    enabledExchanges: ["cryptocom"],
  },
  [API_ENDPOINT.ADMIN_CASHBACK_CONFIG]: {
    id: "mock-cashback-config-id",
    cashbackRate: 0.25,
    unlockThresholdUsd: 5.0,
    monthlyCapUsd: 500.0,
    isActive: true,
    enabledExchanges: ["cryptocom"],
    enabledContexts: {
      tradePage: true,
      campaigns: true,
    },
    exchangeRateOverrides: {},
    vaultAlertEnabled: true,
    createdAt: new Date("2025-01-01T00:00:00Z"),
    updatedAt: new Date("2025-01-01T00:00:00Z"),
  },
  [API_ENDPOINT.SPOT_ORDERS]: {
    orders: [],
    total: 0,
    limit: 50,
    offset: 0,
  },
  [API_ENDPOINT.SPOT_TRADES]: {
    trades: [],
    total: 0,
    limit: 50,
    offset: 0,
  },
  // Automatic Streak endpoints
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_CREATE]: {
    streakId: "mock-streak-id-123",
    status: "PENDING",
    scheduledStartTime: new Date().toISOString(),
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_CREATE_V2]: {
    streakId: "mock-streak-id-v2-123",
    status: "PENDING",
    scheduledStartTime: new Date().toISOString(),
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_LIST]: [],
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_GET]: {
    id: "mock-streak-id-123",
    hubUserId: 1,
    campaignName: "test-campaign",
    epochId: 1,
    exchangeAccountId: 1,
    pairSymbol: "BTC/USDT",
    totalTrades: 10,
    volumeMin: "10",
    volumeMax: "50",
    intervalSeconds: 30,
    scheduledStartTime: new Date().toISOString(),
    status: "PENDING",
    completedTrades: 0,
    skippedCycles: 0,
    totalVolumeTraded: "0",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_CANCEL]: {
    streakId: "mock-streak-id-123",
    status: "CANCELLED",
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_CONFIG]: {
    config: {
      enabled: true,
      minTrades: 1,
      maxTrades: 20,
      minVolumePerTrade: 5,
      maxVolumePerTrade: 50,
      minVolumeDifference: 5,
      validIntervals: [30, 60, 120],
      defaultStartDelayMinutes: 0,
      defaultCycles: 20,
      volumeVariancePercent: 25,
      defaultVolumePerTrade: 10,
    },
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_CGU_STATUS]: {
    acknowledged: false,
  },
  // V2 endpoints
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_LIST_V2]: [],
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_GET_V2]: {
    id: "mock-streak-id-v2-123",
    hubUserId: 1,
    campaignName: "test-campaign",
    epochId: 1,
    exchangeAccountId: 1,
    pairSymbol: "BTC/USDT",
    totalTargetVolume: "500",
    timeframeMs: 3600000,
    scheduledStartTime: new Date().toISOString(),
    status: "PENDING",
    completedTrades: 0,
    skippedCycles: 0,
    totalVolumeTraded: "0",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: "V2" as const,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_CANCEL_V2]: {
    streakId: "mock-streak-id-v2-123",
    status: "CANCELLED",
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_EPOCH_LIMIT]: {
    isLimitReached: false,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_RECENTLY_CANCELLED]: [],
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_MINIMUM_REQUIRED_BALANCE]: {
    minimumRequiredBalance: 25,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_JOIN_QUEUE_V2]: {
    status: "QUEUED" as const,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_LEAVE_QUEUE_V2]: {
    status: "LEFT" as const,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_GET_QUEUE_ITEM_V2]: {
    status: null,
    joinedAt: null,
    leaveReason: null,
    leftAt: null,
    priorityCounter: 0,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_QUEUE_PRIORITY_TOPUP_V2]: {
    status: "PRIORITIZED" as const,
  },
  [API_ENDPOINT.HUB_AUTOMATIC_STREAK_PRIORITY_TICKETS]: {
    totalTickets: 0,
  },
  [API_ENDPOINT.ADMIN_AUTOMATIC_STREAK_V2_GRANT_PRIORITY_TICKETS]: {
    status: "GRANTED" as const,
    totalTickets: 0,
  },
  [API_ENDPOINT.ADMIN_HUB_USERS]: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },
  [API_ENDPOINT.ADMIN_HUB_USERS_UPDATE]: {
    success: true,
  },
  [API_ENDPOINT.ADMIN_HUB_USERS_AVATAR]: { success: true },
  [API_ENDPOINT.ADMIN_HUB_USERS_QUOTE]: { success: true },
  [API_ENDPOINT.ADMIN_BLACKLIST]: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },
  [API_ENDPOINT.ADMIN_BLACKLIST_BATCH_DELETE]: {
    success: true,
  },
  [API_ENDPOINT.ADMIN_WHITELIST]: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },
  [API_ENDPOINT.ADMIN_AUTOMATIC_STREAK_CONFIG]: {
    enabled: true,
    minTrades: 1,
    maxTrades: 20,
    minVolumePerTrade: 5,
    maxVolumePerTrade: 50,
    minVolumeDifference: 5,
    validIntervals: [30, 60, 120],
    defaultStartDelayMinutes: 0,
    defaultCycles: 20,
    volumeVariancePercent: 25,
    defaultVolumePerTrade: 10,
  },
  [API_ENDPOINT.ADMIN_AUTOMATIC_STREAK_V2_EPOCHS]: {
    epochIds: [],
  },
  [API_ENDPOINT.ADMIN_AUTOMATIC_STREAK_V2_USERS]: {
    users: [],
  },
  [API_ENDPOINT.ADMIN_AUTOMATIC_STREAK_V2_TRADES]: {
    users: [],
  },
  [API_ENDPOINT.ADMIN_AUTOMATIC_STREAK_V2_QUEUE_ITEMS]: {
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    inQueueCount: 0,
    kickedCount: 0,
    voluntaryLeftCount: 0,
  },
  [API_ENDPOINT.QUEST_CMS_SAVE]: {
    message: "Quest saved successfully",
  },
  [API_ENDPOINT.QUEST_CMS_DELETE]: {
    message: "Quest deleted successfully",
  },
  [API_ENDPOINT.QUEST_CMS_GET_BY_ID]: {
    id: "1",
    questId: 1,
    name: "Test Quest",
    description: "Test Quest Description",
    app: QUEST_APP.HUB,
    status: PublicationStatus.DRAFT,
    rewardCount: new Decimal("100"),
    rewardType: QUEST_REWARD_TYPE.LOTTERY_TICKET,
    rewardDistribution: QUEST_REWARD_DISTRIBUTION.CLAIMABLE,
    typeConfig: {
      type: QUEST_TYPE.SOCIAL_CONNECTION,
      data: {
        connectSocial: "discord",
      },
    },
    frequency: QUEST_FREQUENCY.DAILY,
    startDate: new Date(),
    endDate: new Date(),
    windowAnchor: QUEST_WINDOW_ANCHOR.QUEST_START,
  },
  [API_ENDPOINT.QUEST_CMS_GET_BY_PAGE]: [],
  [API_ENDPOINT.ADMIN_ONCHAIN_CONFIG]: {
    id: "mock-onchain-config-id",
    walletAgeCheckEnabled: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.HUB_TRADE_MARKET_PAIRS_V2]: [],
  [API_ENDPOINT.POLYMARKET_CREDENTIALS]: undefined,
  [API_ENDPOINT.POLYMARKET_CREDENTIALS_CHECK]: {
    exists: false,
  },
  [API_ENDPOINT.POLYMARKET_ORDERS]: undefined,
  [API_ENDPOINT.POLYMARKET_EVENTS]: [],
  [API_ENDPOINT.POLYMARKET_EVENTS_BY_IDS]: [],
  // Feature Flags CMS endpoints
  [API_ENDPOINT.FEATURE_FLAGS_CMS_CREATE]: {
    message: "Feature flag created successfully",
  },
  [API_ENDPOINT.FEATURE_FLAGS_CMS_UPDATE]: {
    message: "Feature flag updated successfully",
  },
  [API_ENDPOINT.FEATURE_FLAGS_CMS_DELETE]: {
    message: "Feature flag deleted successfully",
  },
  [API_ENDPOINT.FEATURE_FLAGS_CMS_BULK_CREATE]: {
    message: "Feature flags created successfully",
    count: 0,
  },
  [API_ENDPOINT.FEATURE_FLAGS_CMS_BULK_DELETE]: {
    message: "Feature flags deleted successfully",
    count: 0,
  },
  [API_ENDPOINT.FEATURE_FLAGS_CMS_GET_BY_PAGE]: {
    data: [],
    total: 0,
    page: 1,
    size: 20,
  },
  [API_ENDPOINT.ADMIN_CPC_CREATE_SIMULATION]: {
    success: true,
  },
  [API_ENDPOINT.ADMIN_CPC_LIST_SIMULATIONS]: {
    simulations: [],
  },
  [API_ENDPOINT.ADMIN_CPC_CANCEL_SIMULATION]: {
    success: true,
  },
  [API_ENDPOINT.ADMIN_CPC_VERIFY_WALLET]: {
    match: true,
    simulationWalletAddress: "0x0000000000000000000000000000000000000000",
  },
  [API_ENDPOINT.ADMIN_CPC_WALLET_PORTFOLIO]: {
    data: {
      total_wallet_balance: 0,
      wallets: [],
      assets: [],
      balances_length: 0,
    },
  },
  // Self-Register Campaigns endpoints
  [API_ENDPOINT.HUB_SELF_REGISTER_CAMPAIGNS_SUBMIT]: undefined,
  [API_ENDPOINT.CONSENT_REGION]: { requiresConsent: true, countryCode: "FR" },
  [API_ENDPOINT.CONSENT_RECORD]: undefined,
  [API_ENDPOINT.SERVER_TIME]: { now: 0 },
  // Crypto.com Referee endpoints
  [API_ENDPOINT.HUB_CRYPTOCOM_REFEREE_SUBMIT]: undefined,
  // CMS Permissions endpoints
  [API_ENDPOINT.CMS_PERMISSIONS_ME]: {
    collections: [],
    isSuperAdmin: false,
  },
  [API_ENDPOINT.CMS_PERMISSIONS_USERS]: [],
  // Airdrop Farming
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BOTS]: [],
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BOTS_V2]: { bots: [], hasMore: false, nextOffset: null },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BOT_AGGREGATES]: {
    totalVolumeUsdc: "0",
    totalFeesUsdc: "0",
    totalPnlUsdc: "0",
    totalOpenInterestUsdc: "0",
    totalBots: 0,
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BOT_PERFORMANCE]: {
    perExchange: [],
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_MIN_CAPITAL]: {
    minCapitalUsdc: "27.00",
    leverage: 3,
    numLevels: 3,
    maxLossUsdc: "5",
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_AVAILABLE_CAPITAL]: {
    availableForNewBotUsdc: "0.00",
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_SUGGEST_PAIRS]: {
    exchangeId: "tradexyz",
    pairs: [],
    fetchedAt: new Date().toISOString(),
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_FEE_TIER]: null,
  [API_ENDPOINT.HUB_AIRDROP_FARMING_LEADERBOARD]: {
    entries: [],
    pagination: { page: 1, limit: 30, total: 0 },
    exchanges: [],
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_LEADERBOARD_STATS]: {
    totalVolumeUsdc: "0",
    totalTraders: 0,
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_LEADERBOARD_POSITION]: null,
  [API_ENDPOINT.HUB_AIRDROP_FARMING_PENDING_RATING]: { pendingBotId: null, pendingBotType: null },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BOT_RATING]: { success: true },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_BOT_HIDE]: { success: true },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD]: { entries: [], totalTraders: 0 },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD_POSITION]: null,
  [API_ENDPOINT.HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD_STATS]: {
    totalVolumeUsd: "0",
    totalTraders: 0,
  },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_ORDER_FILL_LEADERBOARD_VOLUMES]: { volumeBySlug: {} },
  [API_ENDPOINT.HUB_AIRDROP_FARMING_MAINTENANCE_WINDOWS]: { window: null },
  // Delta-Neutral
  [API_ENDPOINT.HUB_DELTA_NEUTRAL_BOTS]: { bots: [] },
  [API_ENDPOINT.HUB_DELTA_NEUTRAL_LEG_INFO]: {
    freeBalanceUsdc: "0",
    fundingRate: "0",
    minNotionalUsdc: null,
  },
  [API_ENDPOINT.HUB_DELTA_NEUTRAL_BOT_HIDE]: { success: true },
  //-------- HUB BANNERS --------
  [API_ENDPOINT.HUB_BANNER_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.HUB_BANNER_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.HUB_BANNER_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    name: "Mock Hub Banner",
    startDate: new Date(),
    endDate: new Date(),
    publicationStatus: PublicationStatus.DRAFT,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.HUB_BANNER_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.HUB_BANNERS_ACTIVE]: [],
  //-------- TRADE AND EARN COMPETITIONS --------
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    slug: "mock-competition",
    title: "Mock Competition",
    description: "Mock description",
    exchangeId: "hyperliquid",
    startDate: new Date(),
    endDate: new Date(),
    prizePool: "$10,000 USDT",
    rewardSymbol: "$",
    coverImageFileId: null,
    coverImageUrl: null,
    volumeTiers: [] as VolumeTier[],
    pointBoostTiers: [] as PointBoostTier[],
    publicationStatus: PublicationStatus.DRAFT,
    showBannerOnFlow: false,
    bannerColor: null,
    bannerExtraReward: null,
    allowedSymbols: [],
    groupSlug: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  } satisfies CedeFlowTradeAndEarnCompetitionDto,
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITIONS_ACTIVE]: { competitions: [], groupAggregates: {} },
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_BY_SLUG]: {
    id: "00000000-0000-0000-0000-000000000001",
    slug: "mock-competition",
    title: "Mock Competition",
    description: "Mock description",
    exchangeId: "hyperliquid",
    startDate: new Date(),
    endDate: new Date(),
    prizePool: "$10,000 USDT",
    rewardSymbol: "$",
    coverImageFileId: null,
    coverImageUrl: null,
    volumeTiers: [] as VolumeTier[],
    pointBoostTiers: [] as PointBoostTier[],
    publicationStatus: PublicationStatus.PUBLISHED,
    showBannerOnFlow: false,
    bannerColor: null,
    bannerExtraReward: null,
    allowedSymbols: [],
    groupSlug: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  } satisfies CedeFlowTradeAndEarnCompetitionDto,
  [API_ENDPOINT.CEDE_FLOW_TRADE_AND_EARN_COMPETITION_BY_SLUG_INTERNAL]: {
    id: "00000000-0000-0000-0000-000000000001",
    slug: "mock-competition",
    title: "Mock Competition",
    description: "Mock description",
    exchangeId: "hyperliquid",
    startDate: new Date(),
    endDate: new Date(),
    prizePool: "$10,000 USDT",
    rewardSymbol: "$",
    coverImageFileId: null,
    coverImageUrl: null,
    volumeTiers: [] as VolumeTier[],
    pointBoostTiers: [] as PointBoostTier[],
    publicationStatus: PublicationStatus.PUBLISHED,
    showBannerOnFlow: false,
    bannerColor: null,
    bannerExtraReward: null,
    allowedSymbols: [],
    groupSlug: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  } satisfies CedeFlowTradeAndEarnCompetitionDto,
  //-------- HUB UPDATES --------
  [API_ENDPOINT.HUB_UPDATE_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.HUB_UPDATE_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.HUB_UPDATE_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    title: "Mock Hub Update",
    description: "Mock description",
    publicationStatus: PublicationStatus.DRAFT,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.HUB_UPDATE_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.HUB_UPDATES_PUBLISHED]: [],
  //-------- ARENA ANNOUNCEMENTS --------
  [API_ENDPOINT.ARENA_ANNOUNCEMENT_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.ARENA_ANNOUNCEMENT_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.ARENA_ANNOUNCEMENT_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    title: "Mock Arena Announcement",
    description: "Mock description",
    targetUtms: [],
    publicationStatus: PublicationStatus.DRAFT,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.ARENA_ANNOUNCEMENT_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.ARENA_ANNOUNCEMENTS_PUBLISHED]: [],
  //-------- GEO RESTRICTIONS --------
  [API_ENDPOINT.GEO_RESTRICTION_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.GEO_RESTRICTION_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.GEO_RESTRICTION_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  //-------- ONBOARDING STEPS --------
  [API_ENDPOINT.ONBOARDING_STEP_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.ONBOARDING_STEP_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.ONBOARDING_STEP_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    stepNumber: 0,
    publicationStatus: PublicationStatus.DRAFT,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.ONBOARDING_STEP_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.ONBOARDING_STEPS_PUBLISHED]: [],
  //-------- TRADE TUTORIAL STEPS --------
  [API_ENDPOINT.TRADE_TUTORIAL_STEP_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.TRADE_TUTORIAL_STEP_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.TRADE_TUTORIAL_STEP_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    stepNumber: 0,
    publicationStatus: PublicationStatus.DRAFT,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.TRADE_TUTORIAL_STEP_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.TRADE_TUTORIAL_STEPS_PUBLISHED]: [],
  //-------- SPINNING WHEEL CONFIGS --------
  [API_ENDPOINT.SPINNING_WHEEL_CONFIG_CMS_SAVE]: { message: "ok" },
  [API_ENDPOINT.SPINNING_WHEEL_CONFIG_CMS_DELETE]: { message: "ok" },
  [API_ENDPOINT.SPINNING_WHEEL_CONFIG_CMS_GET_BY_ID]: {
    id: "00000000-0000-0000-0000-000000000001",
    name: "Mock Spinning Wheel Config",
    startDate: new Date(),
    isActive: false,
    minimumClaimThresholdUsd: 0,
    slots: [
      { amount: 100, tokenSymbol: "USDT", probability: "0.5" },
      { amount: 50, tokenSymbol: "USDT", probability: "0.5" },
    ],
    publicationStatus: PublicationStatus.DRAFT,
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  [API_ENDPOINT.SPINNING_WHEEL_CONFIG_CMS_GET_BY_PAGE]: { data: [], total: 0, page: 0, size: 20 },
  [API_ENDPOINT.FUNDING_RATE_SUMMARIES]: {
    items: [],
    supportedExchangeIds: [],
    pagination: { page: 0, limit: 20, total: 0 },
  },
  [API_ENDPOINT.HUB_PAPER_TRADING_PLACE_ORDER]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_CANCEL_ORDER]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_CANCEL_ATTACHED_TRIGGERS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_PREVIEW_ORDER]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_ACCOUNT]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_POSITIONS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_SET_POSITION_TRIGGERS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_CLEAR_POSITION_TRIGGERS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_OPEN_ORDERS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_FILLS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_OHLCV]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_RWA_VENUES]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_PAIR_METAS]: {},
  [API_ENDPOINT.HUB_PAPER_TRADING_PAIR_VOLATILITY]: {},
};
